package it.neckar.lizergy.model.price.lizergy

import com.meistercharts.charts.pv.roofPlanning.ModuleSize
import com.meistercharts.charts.pv.roofPlanning.PerModule
import it.neckar.lizergy.lifeCycle.LizergyHasLifeCycle
import it.neckar.lizergy.lifeCycle.LizergyLifeCycleState
import it.neckar.lizergy.model.configuration.components.BatteryConfiguration
import it.neckar.lizergy.model.configuration.components.BatteryConfiguration.BatteryConfigurationId
import it.neckar.lizergy.model.configuration.components.IndependenceManagerType
import it.neckar.lizergy.model.configuration.components.IndependenceManagerType.IndependenceManagerId
import it.neckar.lizergy.model.configuration.components.LizergyIndependenceManagerType
import it.neckar.lizergy.model.configuration.components.OtherIndependenceManagerType
import it.neckar.lizergy.model.configuration.moduleLayout.PvModule
import it.neckar.lizergy.model.configuration.moduleLayout.PvModule.PvModuleId
import it.neckar.lizergy.model.configuration.moduleLayout.PvModuleTypeMaterial
import it.neckar.lizergy.model.configuration.moduleLayout.roof.MetalRoofTile
import it.neckar.lizergy.model.configuration.moduleLayout.roof.MetalRoofTileColor
import it.neckar.lizergy.model.configuration.moduleLayout.roof.MetalRoofTileColor.MetalRoofTileColorId
import it.neckar.lizergy.model.configuration.moduleLayout.roof.MetalRoofTileCompatability
import it.neckar.lizergy.model.configuration.moduleLayout.roof.RoofType
import it.neckar.lizergy.model.configuration.moduleLayout.roof.RoofType.RoofTypeId
import it.neckar.lizergy.model.configuration.quote.builder.AssemblyDifficulty
import it.neckar.lizergy.model.configuration.quote.builder.BasicBatteryInverter
import it.neckar.lizergy.model.configuration.quote.builder.BatteryInverter
import it.neckar.lizergy.model.configuration.quote.builder.BatteryStorageExtensionType
import it.neckar.lizergy.model.configuration.quote.builder.BatteryStorageExtensionType.BatteryStorageExtensionTypeId
import it.neckar.lizergy.model.configuration.quote.builder.BatteryStorageType
import it.neckar.lizergy.model.configuration.quote.builder.BatteryStorageType.BatteryStorageTypeId
import it.neckar.lizergy.model.configuration.quote.builder.HeaterRod
import it.neckar.lizergy.model.configuration.quote.builder.HeaterRod.HeaterRodId
import it.neckar.lizergy.model.configuration.quote.builder.HybridInverter
import it.neckar.lizergy.model.configuration.quote.builder.Inverter
import it.neckar.lizergy.model.configuration.quote.builder.InverterType
import it.neckar.lizergy.model.configuration.quote.builder.InverterType.InverterId
import it.neckar.lizergy.model.configuration.quote.builder.Manufacturer
import it.neckar.lizergy.model.configuration.quote.builder.Wallbox
import it.neckar.lizergy.model.configuration.quote.builder.Wallbox.WallboxId
import it.neckar.lizergy.model.energy.BatteryCapacity
import it.neckar.lizergy.model.energy.Capacity
import it.neckar.lizergy.model.energy.ampere
import it.neckar.lizergy.model.energy.kWh
import it.neckar.lizergy.model.energy.volts
import it.neckar.lizergy.model.energy.wattPeak
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.financial.currency.MoneyAndTime
import it.neckar.lizergy.model.financial.currency.PricePerHour
import it.neckar.lizergy.model.financial.currency.PriceWithProfit
import it.neckar.lizergy.model.financial.currency.PriceWithProfitAndWorkingTime
import it.neckar.lizergy.model.financial.currency.WorkingTimesWithPrice
import it.neckar.lizergy.model.financial.currency.euro
import it.neckar.lizergy.model.price.InfoForType
import it.neckar.lizergy.model.price.MonitoringPrices
import it.neckar.lizergy.model.price.OptionalDiscountPrices
import it.neckar.lizergy.model.price.PriceList.PriceListId
import it.neckar.lizergy.model.price.Prices
import it.neckar.lizergy.model.price.RoofIsolationPrices
import it.neckar.lizergy.model.price.StaticPriceList
import it.neckar.open.kotlin.lang.percent
import it.neckar.open.unit.time.h
import it.neckar.uuid.HasUuid
import it.neckar.uuid.xor
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.uuid.Uuid

/**
 * Factory that calculates a price list
 */
@Suppress("DuplicatedCode")
object LizergyPriceListFactory {

  /**
   * The price per working hour
   */
  val workingHourPrice: PricePerHour = PricePerHour.euros(75.0)

  const val workingHoursPerDay: @h Double = 7.0

  val surCharges: SurCharges = SurCharges(
    modulesSurCharge = 40.percent,
    inverterSurCharge = 40.percent,
    batteryStorageSurCharge = 40.percent,
    wallBoxesSurCharge = 40.percent,
    metalRoofTilesSurCharge = 40.percent,
    substructureSurCharge = 40.percent,
    constructionSurCharge = 40.percent,
    waermepumpenSurCharge = 100.percent,
    energyMeterSurCharge = 50.percent,
    schienenSurCharge = 50.percent,
    klemmenSurCharge = 200.percent,
    specialSurCharge = 100.percent,
    blackModulesSurCharge = 50.percent,
    optimizerSurCharge = 40.percent,
    aufschlagKreuzverbundSurCharge = 30.percent,
  )

  /**
   * The module prices
   *
   * The first module in this list will be selected by default when creating a new roof. It is therefore considered the "standard module"
   */
  @Reselling
  val moduleInputPrices: InfoForType<PvModule, Money> = InfoForType {
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("764f5856-4971-4909-9804-2a75ee9bcbc5")),
        description = "IBC Glas-Glas Modul Transparent 465 LS-TZ1",
        powerRating = 465.wattPeak,
        offLoadVoltage = 36.46.volts,
        shortCircuitCurrent = 15.88.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        dataSheetPath = "IBC Module Transparent 465 LS-TZ1 Datenblatt.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
      ), 65.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("c9b0a6f8-0c4f-4d72-91dc-7b2c8f1e4a37")),
        description = "IBC Glas-Glas Modul Transparent 460 LS-TZ1",
        powerRating = 460.wattPeak,
        offLoadVoltage = 36.32.volts,
        shortCircuitCurrent = 15.38.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        dataSheetPath = "IBC Module Transparent 460 LS-TZ1 Datenblatt.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
      ), 60.00.euro
    )

    // Deprecated elements
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("4bccae46-f827-4c3c-abf6-319c90fccbc1")),
        description = "IBC Glas-Glas Modul Transparent 450",
        powerRating = 450.wattPeak,
        offLoadVoltage = 40.25.volts,
        shortCircuitCurrent = 14.17.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        dataSheetPath = "Module Transparent 450 LS-TA2.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 54.50.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("67fface9-85b6-4766-8241-0502b0f82c49")),
        description = "IBC Glas-Glas Modul Transparent 455 ES-TZ1",
        powerRating = 455.wattPeak,
        offLoadVoltage = 35.11.volts,
        shortCircuitCurrent = 15.93.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        dataSheetPath = "IBC Module Transparent 455 ES-TZ1 Datenblatt.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 48.98.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("deecc2d4-9597-4ac9-9662-d3877067b308")),
        description = "IBC Modul White 450",
        powerRating = 450.wattPeak,
        offLoadVoltage = 39.78.volts,
        shortCircuitCurrent = 14.0.ampere,
        size = ModuleSize(1762, 1134),
        dataSheetPath = "Module White 450 MS-TA1.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 58.10.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("dea1355a-39ee-4cf5-a0a8-d6eb6e9e17fd")),
        description = "Jolywood JW-HD96N R2 455 Wp",
        powerRating = 455.wattPeak,
        offLoadVoltage = 35.11.volts,
        shortCircuitCurrent = 15.93.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        dataSheetPath = "JW-HD96N-R2-455 Glas-Glas-Modul.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 50.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("da6fce49-7a71-4c12-91ff-d2f5e9cde143")),
        description = "Jinko JKM450N",
        powerRating = 450.wattPeak,
        offLoadVoltage = 39.78.volts,
        shortCircuitCurrent = 14.0.ampere,
        size = ModuleSize(1762, 1134),
        dataSheetPath = "JKM450N-54HL4R-V.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 56.32.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("550503c6-abba-4467-86af-5461a1ba09fd")),
        description = "DMEGC DM450M10RT-B54HBT Glas-Glas-Modul",
        powerRating = 450.wattPeak,
        offLoadVoltage = 40.25.volts,
        shortCircuitCurrent = 14.17.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        dataSheetPath = "DMEG 450-455 Wp Datenblatt.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 50.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("662a3edc-59cd-4454-8242-b575593034bb")),
        description = "DMEGC DM455M10RT-B54HBT Glas-Glas-Modul",
        powerRating = 455.wattPeak,
        offLoadVoltage = 40.45.volts,
        shortCircuitCurrent = 14.24.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        dataSheetPath = "DMEG 450-455 Wp Datenblatt.pdf",
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 50.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("605d9b36-b8e4-43db-86ee-7419549a74cc")),
        description = "Jinko 445 Wp",
        powerRating = 445.wattPeak,
        offLoadVoltage = 39.59.volts,
        shortCircuitCurrent = 13.93.ampere,
        size = ModuleSize(1762, 1134),
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 60.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("83d8f360-388f-44a5-95b3-bb15cfc65c96")),
        description = "Jolywood 440 Wp Glas-Glas-Modul",
        powerRating = 440.wattPeak,
        offLoadVoltage = 38.60.volts,
        shortCircuitCurrent = 14.25.ampere,
        size = ModuleSize(1762, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 60.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("9048af98-2975-495b-bdf9-e8d65ac17517")),
        description = "Jolywood JW-HD120N-380 Glas-Glas-Modul",
        powerRating = 380.wattPeak,
        offLoadVoltage = 0.volts,
        shortCircuitCurrent = 0.ampere,
        size = ModuleSize(1773, 1046),
        material = PvModuleTypeMaterial.GlassGlass,
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 136.84.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("db79e842-07ac-4292-80e3-863e29ed52db")),
        description = "IBC MonoSol 440",
        powerRating = 440.wattPeak,
        offLoadVoltage = 39.38.volts,
        shortCircuitCurrent = 13.86.ampere,
        size = ModuleSize(1762, 1134),
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 80.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("67a3e375-590c-4296-9aad-a1ce62f59362")),
        description = "Meyer Burger White 390",
        powerRating = 390.wattPeak,
        offLoadVoltage = 0.volts,
        shortCircuitCurrent = 0.ampere,
        size = ModuleSize(1767, 1041),
        warranty = 25,
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 130.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("3ff3f053-8454-448d-bb30-193a4a47124e")),
        description = "Jolywood JW-HD108N-425 Glas-Glas-Modul",
        powerRating = 425.wattPeak,
        offLoadVoltage = 38.10.volts,
        shortCircuitCurrent = 14.05.ampere,
        size = ModuleSize(1722, 1134),
        material = PvModuleTypeMaterial.GlassGlass,
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 80.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("8bb19b31-370d-4d78-8761-c8c7f3f0a549")),
        description = "Jinko 440 Wp",
        powerRating = 440.wattPeak,
        offLoadVoltage = 0.volts,
        shortCircuitCurrent = 0.ampere,
        size = ModuleSize(1762, 1134),
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife
      ), 70.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("3dacc951-b1f7-485a-9378-24ac477aded8")),
        description = "Jinko 425 Wp",
        powerRating = 425.wattPeak,
        offLoadVoltage = 0.volts,
        shortCircuitCurrent = 0.ampere,
        size = ModuleSize(1722, 1134),
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife
      ), 65.00.euro
    )
    add(
      PvModule(
        id = PvModuleId(Uuid.parse("a9e6a229-9331-4f04-83f7-eb8d38159115")),
        description = "IBC MonoSol 425",
        powerRating = 425.wattPeak,
        offLoadVoltage = 38.30.volts,
        shortCircuitCurrent = 14.15.ampere,
        size = ModuleSize(1722, 1134),
        compatibleWithManufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife
      ), 75.00.euro
    )
    add(PvModule(PvModuleId(Uuid.parse("9d09ecc6-d332-453e-a95a-e9fe253be436")), "IBC MonoSol 415", 415.wattPeak, offLoadVoltage = 0.volts, shortCircuitCurrent = 0.ampere, ModuleSize(1722, 1134), compatibleWithManufacturer = Manufacturer.SMA, lifeCycleState = LizergyLifeCycleState.EndOfLife), 147.35.euro)
    add(PvModule(PvModuleId(Uuid.parse("a516b516-0ef1-4df7-af4b-0c93b73ad2cd")), "IBC MonoSol 405", 405.wattPeak, offLoadVoltage = 0.volts, shortCircuitCurrent = 0.ampere, ModuleSize(1722, 1133), compatibleWithManufacturer = Manufacturer.SMA, lifeCycleState = LizergyLifeCycleState.EndOfLife), 139.69.euro)
    add(
      PvModule(PvModuleId(Uuid.parse("90e6470f-1a67-4317-add4-8146b921e60e")), "JinkoSolar MM400V", 400.wattPeak, offLoadVoltage = 0.volts, shortCircuitCurrent = 0.ampere, ModuleSize(1722, 1134), compatibleWithManufacturer = Manufacturer.SMA, lifeCycleState = LizergyLifeCycleState.EndOfLife),
      120.00.euro
    )
    add(PvModule(PvModuleId(Uuid.parse("1f2df20c-12a1-4535-a706-5499456117fe")), "IBC MonoSol 375", 375.wattPeak, offLoadVoltage = 0.volts, shortCircuitCurrent = 0.ampere, ModuleSize(1755, 1038), compatibleWithManufacturer = Manufacturer.SMA, lifeCycleState = LizergyLifeCycleState.EndOfLife), 114.70.euro)
    add(PvModule(PvModuleId(Uuid.parse("50a01ab1-d535-44a7-b90d-dec067c15647")), "IBC MonoSol 370", 370.wattPeak, offLoadVoltage = 0.volts, shortCircuitCurrent = 0.ampere, ModuleSize(1755, 1038), compatibleWithManufacturer = Manufacturer.SMA, lifeCycleState = LizergyLifeCycleState.EndOfLife), 114.70.euro)
    add(PvModule(PvModuleId(Uuid.parse("0df2b036-5e02-4aab-8e89-ed220f23b729")), "IBC MonoSol 340", 340.wattPeak, offLoadVoltage = 0.volts, shortCircuitCurrent = 0.ampere, ModuleSize(1684, 1002), compatibleWithManufacturer = Manufacturer.SMA, lifeCycleState = LizergyLifeCycleState.EndOfLife), 88.40.euro)
    add(PvModule(PvModuleId(Uuid.parse("e664e770-95dc-4448-b304-13854e8df7ca")), "URE 335", 335.wattPeak, offLoadVoltage = 0.volts, shortCircuitCurrent = 0.ampere, ModuleSize(1675, 992), compatibleWithManufacturer = Manufacturer.SMA, lifeCycleState = LizergyLifeCycleState.EndOfLife), 82.50.euro)
  }


  val inverterSB1_5: Inverter = Inverter(InverterId(Uuid.parse("0a112d6c-7900-48e5-ae57-708385fda88d")), "SB 1.5", Manufacturer.SMA, Capacity(1.5), InverterType.Phase.SinglePhase, listOf(1), "Sunny Boy 1.5_2.0_2.5.pdf", lifeCycleState = LizergyLifeCycleState.EndOfLife)
  val inverterSB2_0: Inverter = Inverter(InverterId(Uuid.parse("bfdf60d4-2ef0-48f8-9c2c-3471bdd81091")), "SB 2.0", Manufacturer.SMA, Capacity(2.0), InverterType.Phase.SinglePhase, listOf(1), "Sunny Boy 1.5_2.0_2.5.pdf", lifeCycleState = LizergyLifeCycleState.EndOfLife)
  val inverterSB2_5: Inverter = Inverter(InverterId(Uuid.parse("688b5738-8792-4ed8-8f8e-7522deb22e5c")), "SB 2.5", Manufacturer.SMA, Capacity(2.5), InverterType.Phase.SinglePhase, listOf(1), "Sunny Boy 1.5_2.0_2.5.pdf", lifeCycleState = LizergyLifeCycleState.EndOfLife)
  val inverterSB3_0: Inverter = Inverter(InverterId(Uuid.parse("ba37d13b-b9ca-402a-9aac-2841ea2b2cfd")), "SB 3.0", Manufacturer.SMA, Capacity(3.0), InverterType.Phase.SinglePhase, listOf(1, 1), "Sunny Boy 3.0_3.6_4.0_5.0_6.0.pdf")
  val inverterSB3_6: Inverter = Inverter(InverterId(Uuid.parse("e490928f-12bc-48c8-8349-840aa37c6c50")), "SB 3.6", Manufacturer.SMA, Capacity(3.7), InverterType.Phase.SinglePhase, listOf(1, 1), "Sunny Boy 3.0_3.6_4.0_5.0_6.0.pdf")
  val inverterSB4_0: Inverter = Inverter(InverterId(Uuid.parse("893d43fa-cf77-4802-b70c-38c5645ceff8")), "SB 4.0", Manufacturer.SMA, Capacity(4.0), InverterType.Phase.SinglePhase, listOf(1, 1), "Sunny Boy 3.0_3.6_4.0_5.0_6.0.pdf")
  val inverterSB5_0: Inverter = Inverter(InverterId(Uuid.parse("679f6e60-ff8b-44f0-963c-d92d0048a64b")), "SB 5.0", Manufacturer.SMA, Capacity(4.6), InverterType.Phase.SinglePhase, listOf(1, 1), "Sunny Boy 3.0_3.6_4.0_5.0_6.0.pdf")

  /**
   * The prices for all available inverters
   */
  @Reselling
  val inverterInputPrices: InfoForType<Inverter, Money> = InfoForType {
    add(inverterSB3_0, 676.55.euro)
    add(inverterSB3_6, 720.38.euro)
    add(inverterSB4_0, 800.64.euro)
    add(inverterSB5_0, 810.98.euro)
    add(Inverter(InverterId(Uuid.parse("6ed9bc4e-abdc-4e78-9e19-440a6105a57b")), "STP 6.0", Manufacturer.SMA, Capacity(6.0), InverterType.Phase.ThreePhase, listOf(1, 1), "Sunny Tripower 5.0_6.0.pdf"), 1_180.03.euro)
    add(Inverter(InverterId(Uuid.parse("4f1d76d5-c197-4928-8d1e-fed158faf023")), "STP 8.0", Manufacturer.SMA, Capacity(8.0), InverterType.Phase.ThreePhase, listOf(2, 1), "Sunny Tripower 8.0_10.0.pdf"), 1_416.22.euro)
    add(Inverter(InverterId(Uuid.parse("00b97d10-f526-4766-bf5b-8275746acc87")), "STP 10.0", Manufacturer.SMA, Capacity(10.0), InverterType.Phase.ThreePhase, listOf(2, 1), "Sunny Tripower 8.0_10.0.pdf"), 1_591.56.euro)
    add(Inverter(InverterId(Uuid.parse("1734f783-0415-4d74-9f63-d02e9b0481c8")), "Sunny Tripower X 12", Manufacturer.SMA, Capacity(12.0), InverterType.Phase.ThreePhase, listOf(2, 2, 2), "Sunny Tripower X 12_15_20_25.pdf"), 1_402.99.euro)
    add(Inverter(InverterId(Uuid.parse("42fdbe80-a775-4dd0-9276-7bd832403f6c")), "Sunny Tripower X 15", Manufacturer.SMA, Capacity(15.0), InverterType.Phase.ThreePhase, listOf(2, 2, 2), "Sunny Tripower X 12_15_20_25.pdf"), 1_497.73.euro)
    add(Inverter(InverterId(Uuid.parse("b01007aa-1358-4879-ac0d-50fd08a67be0")), "Sunny Tripower X 20", Manufacturer.SMA, Capacity(20.0), InverterType.Phase.ThreePhase, listOf(2, 2, 2), "Sunny Tripower X 12_15_20_25.pdf"), 1_604.39.euro)
    add(Inverter(InverterId(Uuid.parse("1c2d17f2-1eff-4ad8-aae3-97e565477bcc")), "Sunny Tripower X 25", Manufacturer.SMA, Capacity(25.0), InverterType.Phase.ThreePhase, listOf(2, 2, 2), "Sunny Tripower X 12_15_20_25.pdf"), 1_773.21.euro)

    // Deprecated elements
    add(inverterSB1_5, 512.63.euro)
    add(inverterSB2_0, 531.56.euro)
    add(inverterSB2_5, 691.14.euro)
    add(Inverter(InverterId(Uuid.parse("fb2cdf5a-e5ec-4c74-b8e2-1db6fb560a73")), "Core 1", Manufacturer.SMA, Capacity(50.0), InverterType.Phase.ThreePhase, listOf(2, 2, 2, 2, 2, 2), lifeCycleState = LizergyLifeCycleState.EndOfLife), 4_491.62.euro)
    add(Inverter(InverterId(Uuid.parse("5fcb366b-55b0-4ba7-b6b3-0dbab54a44a8")), "Core 2", Manufacturer.SMA, Capacity(110.0), InverterType.Phase.ThreePhase, listOf(2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2), lifeCycleState = LizergyLifeCycleState.EndOfLife), 5_172.35.euro)
    add(Inverter(InverterId(Uuid.parse("3c3e2a32-29e1-4f3b-8768-2499ae3002a3")), "STP 15.000TL", Manufacturer.SMA, Capacity.Zero, InverterType.Phase.SinglePhase, listOf(), lifeCycleState = LizergyLifeCycleState.EndOfLife), 1798.25.euro)
    add(Inverter(InverterId(Uuid.parse("98318900-b022-47e7-9cf1-8b37614dd981")), "STP 20.000TL", Manufacturer.SMA, Capacity.Zero, InverterType.Phase.SinglePhase, listOf(), lifeCycleState = LizergyLifeCycleState.EndOfLife), 1939.05.euro)
    add(Inverter(InverterId(Uuid.parse("07dac19c-178a-40d6-ad72-b036b8363b82")), "STP 25.000TL", Manufacturer.SMA, Capacity.Zero, InverterType.Phase.SinglePhase, listOf(), lifeCycleState = LizergyLifeCycleState.EndOfLife), 1962.99.euro)
  }

  val hybridInverterSTP5_0: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("89721dd3-750c-43b9-ae60-1202f0ce047b")),
    description = "Sunny Tripower 5.0 Smart Energy",
    manufacturer = Manufacturer.SMA,
    capacity = Capacity(5.0),
    dischargeCapacity = Capacity(6),
    phase = InverterType.Phase.ThreePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1),
    dataSheetPath = "Sunny Tripower Smart Energy 5.0_6.0_8.0_10.0.pdf",
  )
  val hybridInverterSTP6_0: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("1d5e5e6f-6342-4227-a56f-260899faeead")),
    description = "Sunny Tripower 6.0 Smart Energy",
    manufacturer = Manufacturer.SMA,
    capacity = Capacity(6.0),
    dischargeCapacity = Capacity(7.2),
    phase = InverterType.Phase.ThreePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1),
    dataSheetPath = "Sunny Tripower Smart Energy 5.0_6.0_8.0_10.0.pdf",
  )
  val hybridInverterSTP8_0: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("1687a74f-93c0-4310-b118-bd2b986dc749")),
    description = "Sunny Tripower 8.0 Smart Energy",
    manufacturer = Manufacturer.SMA,
    capacity = Capacity(8.0),
    dischargeCapacity = Capacity(10.6),
    phase = InverterType.Phase.ThreePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1),
    dataSheetPath = "Sunny Tripower Smart Energy 5.0_6.0_8.0_10.0.pdf",
  )
  val hybridInverterSTP10_0: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("2bc34719-6da6-4f28-b812-86b2bb59f9c8")),
    description = "Sunny Tripower 10.0 Smart Energy",
    manufacturer = Manufacturer.SMA,
    capacity = Capacity(10.0),
    dischargeCapacity = Capacity(10.6),
    phase = InverterType.Phase.ThreePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 2),
    dataSheetPath = "Sunny Tripower Smart Energy 5.0_6.0_8.0_10.0.pdf",
  )

  val hybridInverterSB3_6: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("2f0e5354-bafb-40b9-81ba-7d082d2c327d")),
    description = "Sunny Boy 3.6 Smart Energy",
    manufacturer = Manufacturer.SMA,
    capacity = Capacity(3.6),
    dischargeCapacity = Capacity(3.789),
    phase = InverterType.Phase.SinglePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1, 1),
    dataSheetPath = "Sunny Boy Smart Energy 3.6_4.0_5.0_6.0.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val hybridInverterSB4_0: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("6c26413b-3ae7-4727-a8be-88539ea90da9")),
    description = "Sunny Boy 4.0 Smart Energy",
    manufacturer = Manufacturer.SMA,
    capacity = Capacity(4.0),
    dischargeCapacity = Capacity(4.211),
    phase = InverterType.Phase.SinglePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1, 1),
    dataSheetPath = "Sunny Boy Smart Energy 3.6_4.0_5.0_6.0.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val hybridInverterSB5_0: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("47311013-a946-4f65-9814-919516088855")),
    description = "Sunny Boy 5.0 Smart Energy",
    manufacturer = Manufacturer.SMA,
    capacity = Capacity(4.6),
    dischargeCapacity = Capacity(5.263),
    phase = InverterType.Phase.SinglePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1, 1),
    dataSheetPath = "Sunny Boy Smart Energy 3.6_4.0_5.0_6.0.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )

  val hybridIBCInverter5: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("2fca68fa-80fa-4c78-a183-04e767ed03de")),
    description = "IBC Inverter 5 AS-H21",
    manufacturer = Manufacturer.IBC,
    capacity = Capacity(5.0),
    dischargeCapacity = Capacity(5),
    phase = InverterType.Phase.ThreePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1),
    dataSheetPath = "Inverter AS-H21 5_6_8_10_12 AS-H31.pdf",
  )
  val hybridIBCInverter6: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("ff7075cd-ad0e-4cbd-8e90-ab5f9b2065aa")),
    description = "IBC Inverter 6 AS-H21",
    manufacturer = Manufacturer.IBC,
    capacity = Capacity(6.0),
    dischargeCapacity = Capacity(6),
    phase = InverterType.Phase.ThreePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1),
    dataSheetPath = "Inverter AS-H21 5_6_8_10_12 AS-H31.pdf",
  )
  val hybridIBCInverter8: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("e38f7c7c-b5c2-461e-93e3-958cc2d4ba4d")),
    description = "IBC Inverter 8 AS-H31",
    manufacturer = Manufacturer.IBC,
    capacity = Capacity(8.0),
    dischargeCapacity = Capacity(8),
    phase = InverterType.Phase.SinglePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1, 1),
    dataSheetPath = "Inverter AS-H21 5_6_8_10_12 AS-H31.pdf",
  )
  val hybridIBCInverter10: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("5778c4a2-fc87-41d9-9b51-a851942f3c5b")),
    description = "IBC Inverter 10 AS-H31",
    manufacturer = Manufacturer.IBC,
    capacity = Capacity(10.0),
    dischargeCapacity = Capacity(10),
    phase = InverterType.Phase.SinglePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1, 1),
    dataSheetPath = "Inverter AS-H21 5_6_8_10_12 AS-H31.pdf",
  )
  val hybridIBCInverter12: HybridInverter = HybridInverter(
    id = InverterId(Uuid.parse("b165f966-8562-47cc-b97e-782adfde7c3a")),
    description = "IBC Inverter 12 AS-H31",
    manufacturer = Manufacturer.IBC,
    capacity = Capacity(12.0),
    dischargeCapacity = Capacity(12),
    phase = InverterType.Phase.SinglePhase,
    numberOfStringsForMppTrackerInput = listOf(1, 1, 1),
    dataSheetPath = "Inverter AS-H21 5_6_8_10_12 AS-H31.pdf",
  )

  @Reselling
  val hybridInverterInputPrices: InfoForType<HybridInverter, Money> = InfoForType {
    add(hybridInverterSTP5_0, 919.00.euro)
    add(hybridInverterSTP6_0, 1_315.12.euro)
    add(hybridInverterSTP8_0, 1_555.20.euro)
    add(hybridInverterSTP10_0, 1_751.09.euro)

    add(hybridIBCInverter5, 915.98.euro)
    add(hybridIBCInverter6, 986.18.euro)
    add(hybridIBCInverter8, 1_101.15.euro)
    add(hybridIBCInverter10, 1_215.00.euro)
    add(hybridIBCInverter12, 1_368.83.euro)

    // Deprecated elements
    add(hybridInverterSB3_6, 947.03.euro)
    add(hybridInverterSB4_0, 992.83.euro)
    add(hybridInverterSB5_0, 1_065.50.euro)
  }

  val batteryInverterSBStorage3_7: BasicBatteryInverter = BasicBatteryInverter(InverterId(Uuid.parse("e9b0ac69-e84d-43ee-ac6d-ab8fab799d10")), "SB Storage 3.7", Manufacturer.SMA, Capacity(3.68), InverterType.Phase.SinglePhase, "Sunny Boy Storage 3.7_5.0_6.0.pdf", lifeCycleState = LizergyLifeCycleState.EndOfLife)
  val batteryInverterSBStorage5_0: BasicBatteryInverter = BasicBatteryInverter(InverterId(Uuid.parse("12313ed4-357c-44dd-8ad7-c75a79bb5e8f")), "SB Storage 5.0", Manufacturer.SMA, Capacity(4.6), InverterType.Phase.SinglePhase, "Sunny Boy Storage 3.7_5.0_6.0.pdf", lifeCycleState = LizergyLifeCycleState.EndOfLife)

  val batteryInverterSunnyIsland4_4: BasicBatteryInverter = BasicBatteryInverter(InverterId(Uuid.parse("4800ef9f-45d0-4148-8671-b250b4768d2d")), "SunnyIsland 4.4", Manufacturer.SMA, Capacity(3.3), InverterType.Phase.SinglePhase, "Sunny Island 4.4M_6.0H_8.0H.pdf")
  val batteryInverterSunnyIsland6_0: BasicBatteryInverter = BasicBatteryInverter(InverterId(Uuid.parse("e17bcc46-3ed7-4b67-a03d-d1be3b50e79e")), "SunnyIsland 6.0", Manufacturer.SMA, Capacity(4.6), InverterType.Phase.SinglePhase, "Sunny Island 4.4M_6.0H_8.0H.pdf")

  @Reselling
  val batteryInverterInputPrices: InfoForType<BasicBatteryInverter, Money> = InfoForType {
    add(batteryInverterSunnyIsland4_4, 1_921.33.euro)
    add(batteryInverterSunnyIsland6_0, 2_255.18.euro)

    // Deprecated elements
    add(batteryInverterSBStorage3_7, 1_288.57.euro)
    add(batteryInverterSBStorage5_0, 1_460.66.euro)
  }


  val batteryBYDHVM8_3: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("c8d60e51-e9e9-45e0-b0f7-ef69a271db15")),
    description = "BYD Battery-Box Premium HVM 8.3",
    shortDescription = "BYD HVM",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(8.3.kWh),
    usableStorageCapacity = BatteryCapacity(8.28.kWh),
    numberOfBatteryModules = 3,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val batteryBYDHVM11_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("d0d550cc-407f-4ae0-bdf3-5d0967e6beda")),
    description = "BYD Battery-Box Premium HVM 11.0",
    shortDescription = "BYD HVM",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(11.kWh),
    usableStorageCapacity = BatteryCapacity(11.04.kWh),
    numberOfBatteryModules = 4,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val batteryBYDHVM13_8: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("a96666b9-294a-4c57-bb03-07f8f1765eb6")),
    description = "BYD Battery-Box Premium HVM 13.8",
    shortDescription = "BYD HVM",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(13.8.kWh),
    usableStorageCapacity = BatteryCapacity(13.8.kWh),
    numberOfBatteryModules = 5,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val batteryBYDHVM16_6: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("17e189c2-daa4-4723-ac4f-31bc171de266")),
    description = "BYD Battery-Box Premium HVM 16.6",
    shortDescription = "BYD HVM",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(16.6.kWh),
    usableStorageCapacity = BatteryCapacity(16.56.kWh),
    numberOfBatteryModules = 6,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val batteryBYDHVM19_3: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("1dd27bac-a2b5-4f80-ad17-1927bf37d80a")),
    description = "BYD Battery-Box Premium HVM 19.3",
    shortDescription = "BYD HVM",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(19.3.kWh),
    usableStorageCapacity = BatteryCapacity(19.32.kWh),
    numberOfBatteryModules = 7,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val batteryBYDHVM22_1: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("43ccd0be-5f38-47e0-953d-c8e77d21f1dd")),
    description = "BYD Battery-Box Premium HVM 22.1",
    shortDescription = "BYD HVM",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(22.1.kWh),
    usableStorageCapacity = BatteryCapacity(22.08.kWh),
    numberOfBatteryModules = 8,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )

  val batteryBYDHVS5_1: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("d24ae12d-148f-4210-9cc5-8daef825d847")),
    description = "BYD Battery-Box Premium HVS 5.1",
    shortDescription = "BYD HVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(5.1.kWh),
    usableStorageCapacity = BatteryCapacity(5.12.kWh),
    numberOfBatteryModules = 2,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val batteryBYDHVS7_7: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("7f9716a4-3800-464d-87af-298f2c3ff38c")),
    description = "BYD Battery-Box Premium HVS 7.7",
    shortDescription = "BYD HVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(7.7.kWh),
    usableStorageCapacity = BatteryCapacity(7.68.kWh),
    numberOfBatteryModules = 3,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )
  val batteryBYDHVS10_2: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("4b2a84c8-edb6-415c-b5b5-447d25d22ee1")),
    description = "BYD Battery-Box Premium HVS 10.2",
    shortDescription = "BYD HVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(10.2.kWh),
    usableStorageCapacity = BatteryCapacity(10.24.kWh),
    numberOfBatteryModules = 4,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS_HVM.pdf",
    lifeCycleState = LizergyLifeCycleState.EndOfLife,
  )

  val batteryBYDHVMPlus8_3: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("ac9ddba2-21c3-4494-bb45-fae71bfd30f2")),
    description = "BYD Battery-Box Premium HVM+ 8.3",
    shortDescription = "BYD HVM+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(8.28.kWh),
    usableStorageCapacity = BatteryCapacity(8.28.kWh),
    numberOfBatteryModules = 3,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVM+.pdf",
  )
  val batteryBYDHVMPlus11_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("0ae58e0d-ecae-4738-8875-0b570d5825bd")),
    description = "BYD Battery-Box Premium HVM+ 11.0",
    shortDescription = "BYD HVM+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(11.04.kWh),
    usableStorageCapacity = BatteryCapacity(11.04.kWh),
    numberOfBatteryModules = 4,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVM+.pdf",
  )
  val batteryBYDHVMPlus13_8: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("0c37ea6b-b189-4ba3-bb94-4d9f49fcd8c9")),
    description = "BYD Battery-Box Premium HVM+ 13.8",
    shortDescription = "BYD HVM+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(13.8.kWh),
    usableStorageCapacity = BatteryCapacity(13.8.kWh),
    numberOfBatteryModules = 5,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVM+.pdf",
  )
  val batteryBYDHVMPlus16_6: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("b1db57a2-0517-472a-b352-f12a72ad21d6")),
    description = "BYD Battery-Box Premium HVM+ 16.6",
    shortDescription = "BYD HVM+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(16.56.kWh),
    usableStorageCapacity = BatteryCapacity(16.56.kWh),
    numberOfBatteryModules = 6,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVM+.pdf",
  )
  val batteryBYDHVMPlus19_3: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("86e3688a-4402-4dc8-be69-aff25b0a7d66")),
    description = "BYD Battery-Box Premium HVM+ 19.3",
    shortDescription = "BYD HVM+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(19.32.kWh),
    usableStorageCapacity = BatteryCapacity(19.32.kWh),
    numberOfBatteryModules = 7,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVM+.pdf",
  )
  val batteryBYDHVMPlus22_1: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("c6e7ee23-792f-40a6-a723-2d22901fd92a")),
    description = "BYD Battery-Box Premium HVM+ 22.1",
    shortDescription = "BYD HVM+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(22.08.kWh),
    usableStorageCapacity = BatteryCapacity(22.08.kWh),
    numberOfBatteryModules = 8,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVM+.pdf",
  )

  val batteryBYDHVSPlus5_1: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("cef79720-fdc0-482c-b4e8-8d06519614fb")),
    description = "BYD Battery-Box Premium HVS+ 5.1",
    shortDescription = "BYD HVS+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(5.12.kWh),
    usableStorageCapacity = BatteryCapacity(5.12.kWh),
    numberOfBatteryModules = 2,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS+.pdf",
  )
  val batteryBYDHVSPlus7_7: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("8f87a0fc-9c40-431d-89b0-dff118c0e4b4")),
    description = "BYD Battery-Box Premium HVS+ 7.7",
    shortDescription = "BYD HVS+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(7.68.kWh),
    usableStorageCapacity = BatteryCapacity(7.68.kWh),
    numberOfBatteryModules = 3,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS+.pdf",
  )
  val batteryBYDHVSPlus10_2: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("e3fd05e5-6c2d-4761-9445-776b1efdd0ce")),
    description = "BYD Battery-Box Premium HVS+ 10.2",
    shortDescription = "BYD HVS+",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(10.24.kWh),
    usableStorageCapacity = BatteryCapacity(10.24.kWh),
    numberOfBatteryModules = 4,
    compatibleWithIndependenceManager = false,
    dataSheetPath = "Battery-Box Premium HVS+.pdf",
  )

  val batteryBYDLVS4_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("5a852ffb-4847-4516-83ce-c1318df04871")),
    description = "BYD Battery-Box Premium LVS M 4.0",
    shortDescription = "BYD LVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(4.kWh),
    usableStorageCapacity = BatteryCapacity(4.0.kWh),
    numberOfBatteryModules = 1,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "Battery-Box Premium LVS V2.1.pdf",
  )
  val batteryBYDLVS8_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("84b974d8-d444-4f45-93d1-656fa6734176")),
    description = "BYD Battery-Box Premium LVS M 8.0",
    shortDescription = "BYD LVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(8.kWh),
    usableStorageCapacity = BatteryCapacity(8.0.kWh),
    numberOfBatteryModules = 2,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "Battery-Box Premium LVS V2.1.pdf",
  )
  val batteryBYDLVS12_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("b6b6c8c2-5437-4860-a17c-456d9485132c")),
    description = "BYD Battery-Box Premium LVS M 12.0",
    shortDescription = "BYD LVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(12.kWh),
    usableStorageCapacity = BatteryCapacity(12.0.kWh),
    numberOfBatteryModules = 3,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "Battery-Box Premium LVS V2.1.pdf",
  )
  val batteryBYDLVS16_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("08e80824-e220-4914-b555-e457f69a4dcd")),
    description = "BYD Battery-Box Premium LVS M 16.0",
    shortDescription = "BYD LVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(16.kWh),
    usableStorageCapacity = BatteryCapacity(16.0.kWh),
    numberOfBatteryModules = 4,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "Battery-Box Premium LVS V2.1.pdf",
  )
  val batteryBYDLVS20_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("6059e3bb-48a8-40a2-8792-28e507ea84fd")),
    description = "BYD Battery-Box Premium LVS M 20.0",
    shortDescription = "BYD LVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(20.kWh),
    usableStorageCapacity = BatteryCapacity(20.0.kWh),
    numberOfBatteryModules = 5,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "Battery-Box Premium LVS V2.1.pdf",
  )
  val batteryBYDLVS24_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("958f30b5-8a0a-4fb3-af92-38dad27f9401")),
    description = "BYD Battery-Box Premium LVS M 24.0",
    shortDescription = "BYD LVS",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(24.kWh),
    usableStorageCapacity = BatteryCapacity(24.0.kWh),
    numberOfBatteryModules = 6,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "Battery-Box Premium LVS V2.1.pdf",
  )

  val batteryBYDLVL15_4: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("9d99907e-7565-4153-9b42-c290a20d75d4")),
    description = "BYD Battery-Box Premium LVL M 15.4",
    shortDescription = "BYD LVL",
    manufacturer = Manufacturer.SMA,
    storageCapacity = BatteryCapacity(15.4.kWh),
    usableStorageCapacity = BatteryCapacity(15.36.kWh),
    numberOfBatteryModules = 1,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "Battery-Box Premium LVL.pdf",
  )

  // Battery extensions
  val batteryBYDLVL15_4Extension: BatteryStorageExtensionType = BatteryStorageExtensionType(
    id = BatteryStorageExtensionTypeId(Uuid.parse("e6b80a46-c1d1-49d1-95db-977ac6ab38c9")),
    description = "BYD Battery-Box Premium LVL M 15.4 mit Parallelschalt-Set",
    storageCapacity = BatteryCapacity(15.4.kWh),
    usableStorageCapacity = BatteryCapacity(15.36.kWh),
    numberOfBatteryModules = 1,
    extensionFor = setOf(batteryBYDLVL15_4),
  )

  // IBC
  val batteryIBCStorage7_5: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("78a26041-dd32-4012-94f3-0b6134a7d575")),
    description = "IBC Storage 7.5 AS-HV1",
    shortDescription = "IBC AS-HV1",
    manufacturer = Manufacturer.IBC,
    storageCapacity = BatteryCapacity(7.68.kWh),
    usableStorageCapacity = BatteryCapacity(6.91.kWh),
    numberOfBatteryModules = 3,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "HomeOneStorage AS_HV1.pdf",
  )
  val batteryIBCStorage10_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("21cb4d19-14c9-4d9b-a490-e31c4605ad7c")),
    description = "IBC Storage 10.0 AS-HV1",
    shortDescription = "IBC AS-HV1",
    manufacturer = Manufacturer.IBC,
    storageCapacity = BatteryCapacity(10.24.kWh),
    usableStorageCapacity = BatteryCapacity(9.21.kWh),
    numberOfBatteryModules = 4,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "HomeOneStorage AS_HV1.pdf",
  )
  val batteryIBCStorage12_5: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("4a047499-6d72-4cfe-9a2c-1870dbaf0fe4")),
    description = "IBC Storage 12.5 AS-HV1",
    shortDescription = "IBC AS-HV1",
    manufacturer = Manufacturer.IBC,
    storageCapacity = BatteryCapacity(12.8.kWh),
    usableStorageCapacity = BatteryCapacity(11.52.kWh),
    numberOfBatteryModules = 5,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "HomeOneStorage AS_HV1.pdf",
  )
  val batteryIBCStorage15_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("a26d473c-ad28-4303-a756-192c3cfb1bf4")),
    description = "IBC Storage 15.0 AS-HV1",
    shortDescription = "IBC AS-HV1",
    manufacturer = Manufacturer.IBC,
    storageCapacity = BatteryCapacity(15.36.kWh),
    usableStorageCapacity = BatteryCapacity(13.82.kWh),
    numberOfBatteryModules = 6,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "HomeOneStorage AS_HV1.pdf",
  )
  val batteryIBCStorage17_5: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("32710b64-766c-4dde-a96e-ef346d62ec5f")),
    description = "IBC Storage 17.5 AS-HV1",
    shortDescription = "IBC AS-HV1",
    manufacturer = Manufacturer.IBC,
    storageCapacity = BatteryCapacity(17.92.kWh),
    usableStorageCapacity = BatteryCapacity(16.12.kWh),
    numberOfBatteryModules = 7,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "HomeOneStorage AS_HV1.pdf",
  )
  val batteryIBCStorage20_0: BatteryStorageType = BatteryStorageType(
    id = BatteryStorageTypeId(Uuid.parse("02deb923-3dfc-4f0d-9e30-25d55d973c55")),
    description = "IBC Storage 20.0 AS-HV1",
    shortDescription = "IBC AS-HV1",
    manufacturer = Manufacturer.IBC,
    storageCapacity = BatteryCapacity(20.48.kWh),
    usableStorageCapacity = BatteryCapacity(18.43.kWh),
    numberOfBatteryModules = 8,
    compatibleWithIndependenceManager = true,
    dataSheetPath = "HomeOneStorage AS_HV1.pdf",
  )

  // Deprecated elements
  val batteryLGChem6_5: BatteryStorageType =
    BatteryStorageType(
      id = BatteryStorageTypeId(Uuid.parse("0ac29d33-9b89-4090-81f9-18a82bddacc1")),
      description = "LG Chem 6.5",
      shortDescription = "LG Chem",
      manufacturer = Manufacturer.SMA,
      storageCapacity = BatteryCapacity(6.5.kWh),
      usableStorageCapacity = BatteryCapacity(6.5.kWh),
      compatibleWithIndependenceManager = false,
      numberOfBatteryModules = 0,
      lifeCycleState = LizergyLifeCycleState.EndOfLife
    )
  val batteryLGChem10_0: BatteryStorageType =
    BatteryStorageType(
      id = BatteryStorageTypeId(Uuid.parse("4c8174c5-d60a-4563-b4d5-e8870df656df")),
      description = "LG Chem 10.0",
      shortDescription = "LG Chem",
      manufacturer = Manufacturer.SMA,
      storageCapacity = BatteryCapacity(9.8.kWh),
      usableStorageCapacity = BatteryCapacity(9.8.kWh),
      compatibleWithIndependenceManager = false,
      numberOfBatteryModules = 0,
      lifeCycleState = LizergyLifeCycleState.EndOfLife
    )

  @Reselling
  val batteryStorageInputPrices: InfoForType<BatteryStorageType, Money> = InfoForType {
    add(batteryBYDHVSPlus5_1, 1_643.46.euro)
    add(batteryBYDHVSPlus7_7, 2_274.79.euro)
    add(batteryBYDHVSPlus10_2, 2_906.12.euro)
    add(batteryBYDHVMPlus8_3, 2_443.04.euro)
    add(batteryBYDHVMPlus11_0, 3_130.45.euro)
    add(batteryBYDHVMPlus13_8, 3_817.87.euro)
    add(batteryBYDHVMPlus16_6, 4_505.28.euro)
    add(batteryBYDHVMPlus19_3, 5_192.69.euro)
    add(batteryBYDHVMPlus22_1, 5_880.11.euro)
    add(batteryBYDLVS4_0, 1_144.18.euro)
    add(batteryBYDLVS8_0, 1_980.89.euro)
    add(batteryBYDLVS12_0, 2_817.59.euro)
    add(batteryBYDLVS16_0, 3_654.30.euro)
    add(batteryBYDLVS20_0, 4_419.01.euro)
    add(batteryBYDLVS24_0, 5_327.71.euro)
    add(batteryBYDLVL15_4, 3_743.77.euro)

    add(batteryIBCStorage7_5, 2_143.65.euro)
    add(batteryIBCStorage10_0, 2_695.43.euro)
    add(batteryIBCStorage12_5, 3_241.20.euro)
    add(batteryIBCStorage15_0, 3_789.98.euro)
    add(batteryIBCStorage17_5, 4_338.75.euro)
    add(batteryIBCStorage20_0, 4_887.53.euro)

    // Deprecated elements
    add(batteryBYDHVS5_1, 1_643.46.euro)
    add(batteryBYDHVS7_7, 2_274.79.euro)
    add(batteryBYDHVS10_2, 2_906.12.euro)
    add(batteryBYDHVM8_3, 2_443.04.euro)
    add(batteryBYDHVM11_0, 3_130.45.euro)
    add(batteryBYDHVM13_8, 3_817.87.euro)
    add(batteryBYDHVM16_6, 4_505.28.euro)
    add(batteryBYDHVM19_3, 5_192.69.euro)
    add(batteryBYDHVM22_1, 5_880.11.euro)
    add(batteryLGChem6_5, 3_996.00.euro)
    add(batteryLGChem10_0, 4_783.00.euro)
  }

  val batteryStorageWorkingTimes: InfoForType<BatteryStorageType, Duration> = InfoForType {
    add(batteryBYDHVSPlus5_1, 7.hours)
    add(batteryBYDHVSPlus7_7, 7.5.hours)
    add(batteryBYDHVSPlus10_2, 8.hours)
    add(batteryBYDHVMPlus8_3, 8.hours)
    add(batteryBYDHVMPlus11_0, 8.5.hours)
    add(batteryBYDHVMPlus13_8, 9.hours)
    add(batteryBYDHVMPlus16_6, 9.5.hours)
    add(batteryBYDHVMPlus19_3, 10.hours)
    add(batteryBYDHVMPlus22_1, 10.5.hours)
    add(batteryBYDLVS4_0, 8.hours)
    add(batteryBYDLVS8_0, 8.5.hours)
    add(batteryBYDLVS12_0, 9.hours)
    add(batteryBYDLVS16_0, 9.5.hours)
    add(batteryBYDLVS20_0, 10.hours)
    add(batteryBYDLVS24_0, 10.5.hours)
    add(batteryBYDLVL15_4, 9.5.hours)

    add(batteryIBCStorage7_5, 7.5.hours)
    add(batteryIBCStorage10_0, 8.hours)
    add(batteryIBCStorage12_5, 8.5.hours)
    add(batteryIBCStorage15_0, 9.hours)
    add(batteryIBCStorage17_5, 9.5.hours)
    add(batteryIBCStorage20_0, 10.hours)

    // Deprecated elements
    add(batteryBYDHVS5_1, 7.hours)
    add(batteryBYDHVS7_7, 7.5.hours)
    add(batteryBYDHVS10_2, 8.hours)
    add(batteryBYDHVM8_3, 8.hours)
    add(batteryBYDHVM11_0, 8.5.hours)
    add(batteryBYDHVM13_8, 9.hours)
    add(batteryBYDHVM16_6, 9.5.hours)
    add(batteryBYDHVM19_3, 10.hours)
    add(batteryBYDHVM22_1, 10.5.hours)
    add(batteryLGChem6_5, 12.hours)
    add(batteryLGChem10_0, 12.hours)
  }

  @Reselling
  val batteryStorageExtraInputPrices: InfoForType<BatteryStorageType, Money> = InfoForType {
    add(batteryBYDHVSPlus5_1, 150.euro)
    add(batteryBYDHVSPlus7_7, 150.euro)
    add(batteryBYDHVSPlus10_2, 150.euro)
    add(batteryBYDHVMPlus8_3, 150.euro)
    add(batteryBYDHVMPlus11_0, 150.euro)
    add(batteryBYDHVMPlus13_8, 150.euro)
    add(batteryBYDHVMPlus16_6, 150.euro)
    add(batteryBYDHVMPlus19_3, 150.euro)
    add(batteryBYDHVMPlus22_1, 150.euro)
    add(batteryBYDLVS4_0, 150.euro)
    add(batteryBYDLVS8_0, 150.euro)
    add(batteryBYDLVS12_0, 150.euro)
    add(batteryBYDLVS16_0, 150.euro)
    add(batteryBYDLVS20_0, 150.euro)
    add(batteryBYDLVS24_0, 150.euro)
    add(batteryBYDLVL15_4, 150.euro)

    add(batteryIBCStorage7_5, 150.euro)
    add(batteryIBCStorage10_0, 150.euro)
    add(batteryIBCStorage12_5, 150.euro)
    add(batteryIBCStorage15_0, 150.euro)
    add(batteryIBCStorage17_5, 150.euro)
    add(batteryIBCStorage20_0, 150.euro)

    // Deprecated elements
    add(batteryBYDHVS5_1, 150.euro)
    add(batteryBYDHVS7_7, 150.euro)
    add(batteryBYDHVS10_2, 150.euro)
    add(batteryBYDHVM8_3, 150.euro)
    add(batteryBYDHVM11_0, 150.euro)
    add(batteryBYDHVM13_8, 150.euro)
    add(batteryBYDHVM16_6, 150.euro)
    add(batteryBYDHVM19_3, 150.euro)
    add(batteryBYDHVM22_1, 150.euro)
    add(batteryLGChem6_5, 150.euro)
    add(batteryLGChem10_0, 150.euro)
  }

  @Reselling
  val batteryStorageExtensionInputPrices: InfoForType<BatteryStorageExtensionType, Money> = InfoForType {
    add(batteryBYDLVL15_4Extension, 3_767.76.euro)
  }
  val batteryStorageExtensionWorkingTimes: InfoForType<BatteryStorageExtensionType, Duration> = InfoForType {
    add(batteryBYDLVL15_4Extension, 4.hours)
  }

  @Reselling
  val batteryStorageExtensionExtraInputPrices: InfoForType<BatteryStorageExtensionType, Money> = InfoForType {
    add(batteryBYDLVL15_4Extension, 611.33.euro)
  }

  sealed interface BatteryAndInverterType : HasUuid, LizergyHasLifeCycle {
    val battery: BatteryStorageType?
    val inverter: BatteryInverter

    override val lifeCycleState: LizergyLifeCycleState
      get() = inverter.lifeCycleState.and(battery?.lifeCycleState)
  }

  data class BatteryAndInverter(override val uuid: Uuid, override val battery: BatteryStorageType, override val inverter: BasicBatteryInverter) : BatteryAndInverterType
  data class BatteryAndHybridInverter(override val uuid: Uuid, override val battery: BatteryStorageType?, override val inverter: HybridInverter) : BatteryAndInverterType

  val batteryAndInverterCombinations: MutableList<BatteryAndInverterType> = mutableListOf()

  fun BatteryAndInverterType.add() {
    batteryAndInverterCombinations.add(this)
  }

  init {
    BatteryAndInverter(Uuid.parse("b6dcb8e2-3dcb-4fee-8982-9417fb1406c9"), batteryBYDHVM8_3, batteryInverterSBStorage3_7).add()
    BatteryAndInverter(Uuid.parse("b9a5ed14-f4db-481e-b954-79057b584f73"), batteryBYDHVM11_0, batteryInverterSBStorage3_7).add()
    BatteryAndInverter(Uuid.parse("8c39f1d8-1be0-457b-84dd-d951fc5d9d56"), batteryBYDHVM13_8, batteryInverterSBStorage3_7).add()
    BatteryAndInverter(Uuid.parse("b9b3df4b-07fd-4312-9e3b-2713b8f23b7f"), batteryBYDHVM16_6, batteryInverterSBStorage3_7).add()
    BatteryAndInverter(Uuid.parse("ca13c866-334c-4e37-a2dc-58235050936d"), batteryBYDHVM19_3, batteryInverterSBStorage3_7).add()
    BatteryAndInverter(Uuid.parse("bd1d3ab9-b320-4483-b37a-c00df4feb7d9"), batteryBYDHVM22_1, batteryInverterSBStorage3_7).add()

    BatteryAndInverter(Uuid.parse("c899cca0-ab79-4031-b2f5-4a035d91cf20"), batteryBYDHVS5_1, batteryInverterSBStorage3_7).add()
    BatteryAndInverter(Uuid.parse("e8b44daa-1ee2-4572-91b7-e7748f7318da"), batteryBYDHVS7_7, batteryInverterSBStorage3_7).add()
    BatteryAndInverter(Uuid.parse("ff986a96-9833-45d8-b0d2-2f121e2edd06"), batteryBYDHVS10_2, batteryInverterSBStorage3_7).add()

    BatteryAndInverter(Uuid.parse("23518a5d-6bf8-4be9-ad60-a8dfa3d57746"), batteryBYDHVM8_3, batteryInverterSBStorage5_0).add()
    BatteryAndInverter(Uuid.parse("6a6c6ced-5fbf-4ef9-b9b6-2ea5b3b06874"), batteryBYDHVM11_0, batteryInverterSBStorage5_0).add()
    BatteryAndInverter(Uuid.parse("2a087c2f-2ba2-4926-9696-ac5f752ef8a5"), batteryBYDHVM13_8, batteryInverterSBStorage5_0).add()
    BatteryAndInverter(Uuid.parse("5b2f2f27-481c-4ed2-85a0-f6e6094c5fc4"), batteryBYDHVM16_6, batteryInverterSBStorage5_0).add()
    BatteryAndInverter(Uuid.parse("a994f80e-3e27-4b38-9414-dc88b483b451"), batteryBYDHVM19_3, batteryInverterSBStorage5_0).add()
    BatteryAndInverter(Uuid.parse("103c4d78-370f-4dbc-89a9-bce6dfc5911b"), batteryBYDHVM22_1, batteryInverterSBStorage5_0).add()

    BatteryAndInverter(Uuid.parse("46805e33-b5a0-4655-b487-d3a21c20d102"), batteryBYDHVS5_1, batteryInverterSBStorage5_0).add()
    BatteryAndInverter(Uuid.parse("98492f01-2ba2-4523-80ff-82ccbbd8c098"), batteryBYDHVS7_7, batteryInverterSBStorage5_0).add()
    BatteryAndInverter(Uuid.parse("fcd78b2a-0072-4e3b-8ab8-310074cc707f"), batteryBYDHVS10_2, batteryInverterSBStorage5_0).add()

    BatteryAndInverter(Uuid.parse("db5fe601-2cf0-4e7a-a79d-95592a1e3a08"), batteryBYDLVS4_0, batteryInverterSunnyIsland4_4).add()
    BatteryAndInverter(Uuid.parse("3b06a6c8-5ef4-434d-ab8e-4f847c7cba10"), batteryBYDLVS8_0, batteryInverterSunnyIsland4_4).add()
    BatteryAndInverter(Uuid.parse("f98ed12c-0854-4159-be4c-76d324b25275"), batteryBYDLVS12_0, batteryInverterSunnyIsland4_4).add()
    BatteryAndInverter(Uuid.parse("13c71b60-d1a7-4b35-b3fa-1581246c57de"), batteryBYDLVS16_0, batteryInverterSunnyIsland4_4).add()
    BatteryAndInverter(Uuid.parse("42b8b712-7502-4ed8-8320-a7a5cba51f17"), batteryBYDLVS20_0, batteryInverterSunnyIsland4_4).add()
    BatteryAndInverter(Uuid.parse("0e73547a-b46f-42e3-a8e8-79ccdcfd5bef"), batteryBYDLVS24_0, batteryInverterSunnyIsland4_4).add()

    BatteryAndInverter(Uuid.parse("4386a537-4487-4190-ab67-bbecb4304e92"), batteryBYDLVL15_4, batteryInverterSunnyIsland4_4).add()

    BatteryAndInverter(Uuid.parse("aa691921-66d5-42df-aabd-7c7271166006"), batteryBYDLVS4_0, batteryInverterSunnyIsland6_0).add()
    BatteryAndInverter(Uuid.parse("88f49ff0-a66d-48d1-857d-c3b846ed91e6"), batteryBYDLVS8_0, batteryInverterSunnyIsland6_0).add()
    BatteryAndInverter(Uuid.parse("e78273db-723c-4a8d-bdb0-25b398261e15"), batteryBYDLVS12_0, batteryInverterSunnyIsland6_0).add()
    BatteryAndInverter(Uuid.parse("dc20dc09-2dd9-420a-bfd2-06239a61e363"), batteryBYDLVS16_0, batteryInverterSunnyIsland6_0).add()
    BatteryAndInverter(Uuid.parse("6842adf1-429c-431d-b887-caff2c397c43"), batteryBYDLVS20_0, batteryInverterSunnyIsland6_0).add()
    BatteryAndInverter(Uuid.parse("9600e925-14d3-4f17-b301-d256e4cad26f"), batteryBYDLVS24_0, batteryInverterSunnyIsland6_0).add()

    BatteryAndInverter(Uuid.parse("5bc2439f-4a3b-4ef1-b71e-4fd4b4280d31"), batteryBYDLVL15_4, batteryInverterSunnyIsland6_0).add()

    //Deprecated elements
    BatteryAndInverter(Uuid.parse("0ac29d33-9b89-4090-81f9-18a82bddacc1"), batteryLGChem6_5, batteryInverterSunnyIsland4_4).add()
    BatteryAndInverter(Uuid.parse("4c8174c5-d60a-4563-b4d5-e8870df656df"), batteryLGChem10_0, batteryInverterSunnyIsland4_4).add()


    //Only Hybrid-Inverter
    hybridInverterInputPrices.forEach { inverter, price ->
      BatteryAndHybridInverter(inverter.uuid, null, inverter).add()
    }


    //With Hybrid-Inverter
    BatteryAndHybridInverter(Uuid.parse("a5446780-407e-4b74-a3c1-7cffa85d347e"), batteryBYDHVM11_0, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("c07fb792-7085-4197-9c42-9302b5beef80"), batteryBYDHVM13_8, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("28bf7a60-9ae0-4e59-a3d3-111ad06e6e4a"), batteryBYDHVM16_6, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("269ed5d9-c93f-4870-b649-b796b694355a"), batteryBYDHVM19_3, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("0f64a643-3046-497f-8cc8-045ecdb72231"), batteryBYDHVM22_1, hybridInverterSTP10_0).add()

    BatteryAndHybridInverter(Uuid.parse("c25cc797-79b9-4736-95c2-4843efc98b6b"), batteryBYDHVS5_1, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("09343475-370c-487b-9a76-b5e9696bf3b2"), batteryBYDHVS7_7, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("b163b428-77b6-43ab-981d-970b476400d0"), batteryBYDHVS10_2, hybridInverterSTP10_0).add()

    BatteryAndHybridInverter(Uuid.parse("0689be97-c23e-400d-927d-0a9e6e2e238f"), batteryBYDHVM11_0, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("425e3c14-d9fa-4cc4-a817-53ae2872610c"), batteryBYDHVM13_8, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("1e3d860e-7363-407d-b7e8-fd06ec9bc4f9"), batteryBYDHVM16_6, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("113ac50b-627a-483a-a596-8a8d206b0218"), batteryBYDHVM19_3, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("5bb5e396-6270-4cd6-b912-e2db696e6f3c"), batteryBYDHVM22_1, hybridInverterSTP8_0).add()

    BatteryAndHybridInverter(Uuid.parse("ab7f7b3e-aad7-4d3d-ac12-a3f6999ca0c9"), batteryBYDHVS5_1, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("227b1733-de61-405e-854b-05aa88ef7387"), batteryBYDHVS7_7, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("7c080b8e-5274-483c-bc1c-c62e16c4632b"), batteryBYDHVS10_2, hybridInverterSTP8_0).add()

    BatteryAndHybridInverter(Uuid.parse("2ef27eeb-dfd1-460b-9cdd-38a6cebba5ed"), batteryBYDHVM11_0, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("ae022d2d-b069-45a6-945e-15f85037965a"), batteryBYDHVM13_8, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("23603efb-681b-489f-ad34-44c2ab03b0bf"), batteryBYDHVM16_6, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("639b0553-b408-4235-acda-c5c961b2a0b6"), batteryBYDHVM19_3, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("f52af45d-4293-4d17-85b1-92afc63fec2f"), batteryBYDHVM22_1, hybridInverterSTP6_0).add()

    BatteryAndHybridInverter(Uuid.parse("9ddf079e-6ec8-4898-9afa-47c7968a4528"), batteryBYDHVS5_1, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("b7e8ce1f-552b-48e0-a49e-97f01e223f3c"), batteryBYDHVS7_7, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("95f67df0-564f-4505-bd49-f5d7513fcb09"), batteryBYDHVS10_2, hybridInverterSTP6_0).add()

    BatteryAndHybridInverter(Uuid.parse("48a07589-00e9-4ccc-862e-3d5b40fad3fa"), batteryBYDHVM11_0, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("15c4fa2b-bc90-40fd-90b8-3f9c56f9dbec"), batteryBYDHVM13_8, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("f603fab5-72de-49da-8e40-72f47bfc2895"), batteryBYDHVM16_6, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("09eaade3-5f73-402f-a184-8cbdf5df7050"), batteryBYDHVM19_3, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("fe6f14db-d68c-4384-ba19-2879a685fb29"), batteryBYDHVM22_1, hybridInverterSTP5_0).add()

    BatteryAndHybridInverter(Uuid.parse("fd7a9089-2401-4c7d-9194-e3adae441529"), batteryBYDHVS5_1, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("fe493b7e-58d7-4401-aab3-cf3046de5196"), batteryBYDHVS7_7, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("676c3fc2-2359-4175-91b2-0d76aa94d5e1"), batteryBYDHVS10_2, hybridInverterSTP5_0).add()

    //The + series works with the three-phase STP SE only, so no SB SE and no battery inverter. HVM+ 8.3 stays without any combination.
    BatteryAndHybridInverter(Uuid.parse("4914158f-530c-4cc2-89a9-bd9a47374c6f"), batteryBYDHVMPlus11_0, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("5cfa4792-132e-4d52-916e-7ae8c1526552"), batteryBYDHVMPlus13_8, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("b6c4d039-8355-4f1d-aff1-882aaf9ee899"), batteryBYDHVMPlus16_6, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("b8602ed4-e3d1-4626-adbb-76e548071376"), batteryBYDHVMPlus19_3, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("b1114593-f96e-4c63-8122-7629e420d90a"), batteryBYDHVMPlus22_1, hybridInverterSTP10_0).add()

    BatteryAndHybridInverter(Uuid.parse("96aaf493-bd05-4fa8-8e85-7490e9c1445b"), batteryBYDHVSPlus5_1, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("4dfe35ab-948c-421b-b45b-3121570577d8"), batteryBYDHVSPlus7_7, hybridInverterSTP10_0).add()
    BatteryAndHybridInverter(Uuid.parse("26773830-1d7a-4c4f-9cdc-63090947edbd"), batteryBYDHVSPlus10_2, hybridInverterSTP10_0).add()

    BatteryAndHybridInverter(Uuid.parse("a8b341c5-449d-4086-8914-b0b9ef64053d"), batteryBYDHVMPlus11_0, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("63212651-e154-4eaa-a7c1-f174a1669da4"), batteryBYDHVMPlus13_8, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("862ebe33-0dc1-499f-a44c-82f49dba18cc"), batteryBYDHVMPlus16_6, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("0168f55d-e138-4192-bbdb-6102cd5c3d0c"), batteryBYDHVMPlus19_3, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("403aea9b-7d86-4414-8a01-05176bc7e96a"), batteryBYDHVMPlus22_1, hybridInverterSTP8_0).add()

    BatteryAndHybridInverter(Uuid.parse("6ac117e0-79c7-4736-a418-85d7c69be204"), batteryBYDHVSPlus5_1, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("9ee46d37-8ffc-4c1b-b18f-9bed6184e3cc"), batteryBYDHVSPlus7_7, hybridInverterSTP8_0).add()
    BatteryAndHybridInverter(Uuid.parse("3d66f5bb-d8bf-42e6-ba92-dacf1ede71d4"), batteryBYDHVSPlus10_2, hybridInverterSTP8_0).add()

    BatteryAndHybridInverter(Uuid.parse("c0bace42-a7fc-4868-b72f-2c7ca83ced2d"), batteryBYDHVMPlus11_0, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("f7efab26-86d1-4fd4-83ce-cc246c3ff818"), batteryBYDHVMPlus13_8, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("fb2f9cd7-e7c4-4822-a289-1898a5c44838"), batteryBYDHVMPlus16_6, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("f952fd66-61a2-45df-98ba-e0f58fade90d"), batteryBYDHVMPlus19_3, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("9398d421-730f-4dc4-9d1a-9d77934dc43c"), batteryBYDHVMPlus22_1, hybridInverterSTP6_0).add()

    BatteryAndHybridInverter(Uuid.parse("6cff98c5-d761-4fe6-88ca-a7cd78d34e3d"), batteryBYDHVSPlus5_1, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("e62d53c4-8d34-400a-a79f-16bce12439ce"), batteryBYDHVSPlus7_7, hybridInverterSTP6_0).add()
    BatteryAndHybridInverter(Uuid.parse("baa94d9e-1e33-476b-ae86-be97ad2ce4d7"), batteryBYDHVSPlus10_2, hybridInverterSTP6_0).add()

    BatteryAndHybridInverter(Uuid.parse("cdcd706a-e810-47c3-ac3f-99e237086e29"), batteryBYDHVMPlus11_0, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("75e75f69-d94f-4c9e-ade0-d4688078ec81"), batteryBYDHVMPlus13_8, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("bca6358c-8f18-4482-bc0a-8a0dc280dcef"), batteryBYDHVMPlus16_6, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("bc6a9b13-f457-428a-bee3-a941245113e7"), batteryBYDHVMPlus19_3, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("1cbd86a2-262a-453c-b46e-be19365c1a00"), batteryBYDHVMPlus22_1, hybridInverterSTP5_0).add()

    BatteryAndHybridInverter(Uuid.parse("c4046b1b-34fd-477f-8bca-2e72b47d06c2"), batteryBYDHVSPlus5_1, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("feb0b777-170a-4607-aa69-71d158383ebd"), batteryBYDHVSPlus7_7, hybridInverterSTP5_0).add()
    BatteryAndHybridInverter(Uuid.parse("624fe228-84c7-4d0e-a062-db5304703a38"), batteryBYDHVSPlus10_2, hybridInverterSTP5_0).add()

    BatteryAndHybridInverter(Uuid.parse("601863c1-502c-42cb-8d8b-5c19ab5c2d3d"), batteryBYDHVM8_3, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("7407ce0a-84ba-43f7-b133-9d18bf8b6939"), batteryBYDHVM11_0, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("319c5cb6-c31c-4e65-9d8d-3e1ee5ba288c"), batteryBYDHVM13_8, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("ef090ab0-7a1e-4b9d-84a9-4ccb0fbd4ef0"), batteryBYDHVM16_6, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("d56f57cb-c0a3-4c48-8d17-2b9845ea0e4a"), batteryBYDHVM19_3, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("435ff38a-7375-4dc5-8bbc-09f9ef26a7c3"), batteryBYDHVM22_1, hybridInverterSB3_6).add()

    BatteryAndHybridInverter(Uuid.parse("16b5094f-7ad3-494d-91b2-beb58eaa6933"), batteryBYDHVS5_1, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("8b9f06a3-a6eb-4971-a587-be5543ff8d07"), batteryBYDHVS7_7, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("9a183c78-e842-494b-ab77-b4cb6ec790fc"), batteryBYDHVS10_2, hybridInverterSB3_6).add()

    BatteryAndHybridInverter(Uuid.parse("35c3d13c-b78a-476b-8164-563c81ce6e93"), batteryBYDHVM8_3, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("34f544f8-ec30-41f0-903a-2b882a2442a9"), batteryBYDHVM11_0, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("295a5ee0-e5fe-49c8-bf2e-1de3121ca923"), batteryBYDHVM13_8, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("3150efa7-11f0-4db9-9f61-9802ae25026e"), batteryBYDHVM16_6, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("b77b076a-39dd-4fd6-ae3f-a7cfedc67b93"), batteryBYDHVM19_3, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("46a25ae7-10a3-4c32-860e-21c50212047a"), batteryBYDHVM22_1, hybridInverterSB3_6).add()

    BatteryAndHybridInverter(Uuid.parse("c28d7ac9-9a33-4e33-8da9-92a3b4d815db"), batteryBYDHVS5_1, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("6427533a-9776-4a3d-9f0d-ec7f446f690c"), batteryBYDHVS7_7, hybridInverterSB3_6).add()
    BatteryAndHybridInverter(Uuid.parse("bf91c0c4-ce85-4e31-978b-625044a275ce"), batteryBYDHVS10_2, hybridInverterSB3_6).add()

    BatteryAndHybridInverter(Uuid.parse("5a34661a-8ba8-4c9f-9648-ecf5d5720250"), batteryBYDHVM8_3, hybridInverterSB4_0).add()
    BatteryAndHybridInverter(Uuid.parse("92e76b66-6292-464a-9b03-ee4d28ed7f80"), batteryBYDHVM11_0, hybridInverterSB4_0).add()
    BatteryAndHybridInverter(Uuid.parse("f3f5ac31-3d18-43ff-95ab-8c0d482da5f2"), batteryBYDHVM13_8, hybridInverterSB4_0).add()
    BatteryAndHybridInverter(Uuid.parse("c6100195-72a7-46f8-aee5-9ca4e28fd1aa"), batteryBYDHVM16_6, hybridInverterSB4_0).add()
    BatteryAndHybridInverter(Uuid.parse("c82cb943-5ee8-4959-bb8c-88e09d0aedf2"), batteryBYDHVM19_3, hybridInverterSB4_0).add()
    BatteryAndHybridInverter(Uuid.parse("f5b0e46f-38f5-43fd-9037-7048558cea70"), batteryBYDHVM22_1, hybridInverterSB4_0).add()

    BatteryAndHybridInverter(Uuid.parse("b85c84f9-c602-4465-9126-b43892748a13"), batteryBYDHVS5_1, hybridInverterSB4_0).add()
    BatteryAndHybridInverter(Uuid.parse("516f708e-2332-4a9c-bf7c-a884ce5c278b"), batteryBYDHVS7_7, hybridInverterSB4_0).add()
    BatteryAndHybridInverter(Uuid.parse("707343d0-74a0-43d1-b594-3efec27a9c54"), batteryBYDHVS10_2, hybridInverterSB4_0).add()

    BatteryAndHybridInverter(Uuid.parse("4c872c08-0fcb-419e-97a6-89fd86c82a1d"), batteryBYDHVM8_3, hybridInverterSB5_0).add()
    BatteryAndHybridInverter(Uuid.parse("cd73aa16-9465-4bfa-8d55-63015b83ab94"), batteryBYDHVM11_0, hybridInverterSB5_0).add()
    BatteryAndHybridInverter(Uuid.parse("e5710e0c-6e7f-4c7b-89ba-6b41e2a0a286"), batteryBYDHVM13_8, hybridInverterSB5_0).add()
    BatteryAndHybridInverter(Uuid.parse("102338e9-78f9-4bec-bacd-967b58613362"), batteryBYDHVM16_6, hybridInverterSB5_0).add()
    BatteryAndHybridInverter(Uuid.parse("f9258def-0ff9-4e3f-a553-05bdc6159a16"), batteryBYDHVM19_3, hybridInverterSB5_0).add()
    BatteryAndHybridInverter(Uuid.parse("e7058361-97f1-48fa-9306-f24f3a204d6d"), batteryBYDHVM22_1, hybridInverterSB5_0).add()

    BatteryAndHybridInverter(Uuid.parse("54f8d363-82c8-4754-bb46-724d64ea7bb9"), batteryBYDHVS5_1, hybridInverterSB5_0).add()
    BatteryAndHybridInverter(Uuid.parse("1022d450-80cc-49fc-b1dc-9e3a6b96ab49"), batteryBYDHVS7_7, hybridInverterSB5_0).add()
    BatteryAndHybridInverter(Uuid.parse("417be5fc-9f22-48c8-93b6-8bef760b01da"), batteryBYDHVS10_2, hybridInverterSB5_0).add()

    // IBC
    BatteryAndHybridInverter(Uuid.parse("d1f0b3c2-4a5e-4c8f-9b6d-7f0c8e5b1a2b"), batteryIBCStorage7_5, hybridIBCInverter5).add()
    BatteryAndHybridInverter(Uuid.parse("bafafef2-6a44-48ec-abbe-7784422897c9"), batteryIBCStorage10_0, hybridIBCInverter5).add()
    BatteryAndHybridInverter(Uuid.parse("1964b86f-7c89-42b3-b762-1c4cd71bf7df"), batteryIBCStorage12_5, hybridIBCInverter5).add()
    BatteryAndHybridInverter(Uuid.parse("4be2409a-1979-476d-a0b5-a3f0db77a4c2"), batteryIBCStorage15_0, hybridIBCInverter5).add()
    BatteryAndHybridInverter(Uuid.parse("1dd45154-32fa-4926-9fa6-c1ae8c3d383f"), batteryIBCStorage17_5, hybridIBCInverter5).add()
    BatteryAndHybridInverter(Uuid.parse("79a97832-3357-4730-afe6-49410822bba7"), batteryIBCStorage20_0, hybridIBCInverter5).add()

    BatteryAndHybridInverter(Uuid.parse("c0f1b3d2-4a5e-4c8f-9b6d-7f0c8e5b1a2b"), batteryIBCStorage7_5, hybridIBCInverter6).add()
    BatteryAndHybridInverter(Uuid.parse("a8b7fb5e-7f2f-44b8-994c-9440ea3a8366"), batteryIBCStorage10_0, hybridIBCInverter6).add()
    BatteryAndHybridInverter(Uuid.parse("b030cefa-fa7f-407f-bba0-e391c658e99a"), batteryIBCStorage12_5, hybridIBCInverter6).add()
    BatteryAndHybridInverter(Uuid.parse("a73d624a-0cff-4bbe-9566-74f26b71594c"), batteryIBCStorage15_0, hybridIBCInverter6).add()
    BatteryAndHybridInverter(Uuid.parse("d5fb7b49-3252-4af7-a630-525ecb8ad83a"), batteryIBCStorage17_5, hybridIBCInverter6).add()
    BatteryAndHybridInverter(Uuid.parse("2302ab1a-7690-45d3-8fff-744170f0cc50"), batteryIBCStorage20_0, hybridIBCInverter6).add()

    BatteryAndHybridInverter(Uuid.parse("39517fe6-d637-4806-9bf4-6e1336a19095"), batteryIBCStorage7_5, hybridIBCInverter8).add()
    BatteryAndHybridInverter(Uuid.parse("8a15a1d9-ed8b-4ad1-b37a-d3bc0881cb13"), batteryIBCStorage10_0, hybridIBCInverter8).add()
    BatteryAndHybridInverter(Uuid.parse("b15d731b-e178-470c-98d7-7bd74c333a23"), batteryIBCStorage12_5, hybridIBCInverter8).add()
    BatteryAndHybridInverter(Uuid.parse("3163682f-b1f7-4291-a996-082eb048fa33"), batteryIBCStorage15_0, hybridIBCInverter8).add()
    BatteryAndHybridInverter(Uuid.parse("0e98d6bb-4ecd-423d-8ae1-7c7430812100"), batteryIBCStorage17_5, hybridIBCInverter8).add()
    BatteryAndHybridInverter(Uuid.parse("f0daa91d-5535-4cae-b984-adfb54adf4e0"), batteryIBCStorage20_0, hybridIBCInverter8).add()

    BatteryAndHybridInverter(Uuid.parse("c6cd20af-c2db-499a-b1fa-9e65bccc53d2"), batteryIBCStorage7_5, hybridIBCInverter10).add()
    BatteryAndHybridInverter(Uuid.parse("a0386e70-2c7c-4e83-8ca4-6bb1f0094b7f"), batteryIBCStorage10_0, hybridIBCInverter10).add()
    BatteryAndHybridInverter(Uuid.parse("53de1b88-2f3f-492b-9d7c-9458547a7968"), batteryIBCStorage12_5, hybridIBCInverter10).add()
    BatteryAndHybridInverter(Uuid.parse("f591722a-15fa-4979-bc98-201c4e38ec80"), batteryIBCStorage15_0, hybridIBCInverter10).add()
    BatteryAndHybridInverter(Uuid.parse("18ca3602-b0e8-43fd-be3e-3eebd4d12bd4"), batteryIBCStorage17_5, hybridIBCInverter10).add()
    BatteryAndHybridInverter(Uuid.parse("4271a1a5-3354-4447-a9c8-eef4b69ce64b"), batteryIBCStorage20_0, hybridIBCInverter10).add()

    BatteryAndHybridInverter(Uuid.parse("bb9f2d8a-8793-423d-a7c8-b119f03a4554"), batteryIBCStorage7_5, hybridIBCInverter12).add()
    BatteryAndHybridInverter(Uuid.parse("97394ebf-da4b-4a57-b0d5-a11f48258bad"), batteryIBCStorage10_0, hybridIBCInverter12).add()
    BatteryAndHybridInverter(Uuid.parse("aacd8593-8e9a-48be-8916-8fe3b796f096"), batteryIBCStorage12_5, hybridIBCInverter12).add()
    BatteryAndHybridInverter(Uuid.parse("9a182752-ca48-4b56-93a0-adb9cb0c8b7e"), batteryIBCStorage15_0, hybridIBCInverter12).add()
    BatteryAndHybridInverter(Uuid.parse("5a3b446a-5b91-46eb-8d82-5ccde46c9605"), batteryIBCStorage17_5, hybridIBCInverter12).add()
    BatteryAndHybridInverter(Uuid.parse("809001d5-568d-4ec7-864c-70b59a081f6b"), batteryIBCStorage20_0, hybridIBCInverter12).add()
  }


  val ziegelDach: RoofType = RoofType(RoofTypeId(Uuid.parse("bff1602e-3e42-47d9-abee-d031fa35dcfa")), "Ziegeldach", metalRoofTileCompatability = MetalRoofTileCompatability.Supported, withIsolation = true, requiresSparrenabstand = true, hasSparrenabstand = true)
  val biberschwanzDach: RoofType = RoofType(RoofTypeId(Uuid.parse("8f8114b8-9edb-4038-80a8-3778e3d7d634")), "Biberschwanz", metalRoofTileCompatability = MetalRoofTileCompatability.Required, withIsolation = false, requiresSparrenabstand = true, hasSparrenabstand = true)

  /**
   * The price for the sub structure per module - depending on the roof type.
   * Only material!
   */
  @Reselling
  val substructurePerRoofTypeInputPrices: InfoForType<RoofType, @PerModule Money> = InfoForType {
    add(ziegelDach, 42.86.euro)
    add(RoofType(RoofTypeId(Uuid.parse("17e5b6ee-7874-4f58-aafb-e1b94667440b")), "Trapez-Blechdach", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = true, hasSparrenabstand = false), 42.77.euro)
    add(RoofType(RoofTypeId(Uuid.parse("ff8e8d98-95aa-4b93-a4d2-4dc77e396381")), "Stehfalzdach", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = true, hasSparrenabstand = false), 51.95.euro)
    add(RoofType(RoofTypeId(Uuid.parse("777d940f-0634-44c4-889d-37ecf9799393")), "Stehfalzdach Kupfer", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = true, hasSparrenabstand = false), 61.75.euro)
    add(RoofType(RoofTypeId(Uuid.parse("a3cd6525-0b97-4a27-870e-fd4de3d52881")), "Flachdach", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = false, hasSparrenabstand = true), 47.08.euro)
    add(RoofType(RoofTypeId(Uuid.parse("64e5702d-cb25-45ea-9938-c00cb1841060")), "Unterkonstruktion mit Stockschrauben", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = true, hasSparrenabstand = true), 44.94.euro)
    add(biberschwanzDach, 118.66.euro)
    add(RoofType(RoofTypeId(Uuid.parse("599cbc85-69e0-4d60-aaa8-a7fd24db6e11")), "Kalzipdach", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = true, hasSparrenabstand = false), 72.87.euro)

    add(RoofType(RoofTypeId(Uuid.parse("f27b5543-e92d-49af-80cf-07cde7cea743")), "Blechstehfalzdach Kupfer", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = true, lifeCycleState = LizergyLifeCycleState.EndOfLife), 62.50.euro)
    add(RoofType(RoofTypeId(Uuid.parse("7864a0d4-dc88-43b4-8c43-89671e749ace")), "Flachdach Bitumen", metalRoofTileCompatability = MetalRoofTileCompatability.NotSupported, withIsolation = false, requiresSparrenabstand = false, lifeCycleState = LizergyLifeCycleState.EndOfLife), 47.08.euro)
  }

  @Reselling
  @PerModule
  val roofIsolationThicknessPrices: RoofIsolationPrices = RoofIsolationPrices(
    inputPriceBelow4cm = 0.0.euro,
    inputPriceBelow6cm = 0.87.euro,
    inputPriceBelow10cm = 6.71.euro,
    inputPriceBelow14cm = 5.37.euro,
    inputPriceBelow16cm = 8.52.euro,
    inputPriceBelow18cm = 8.87.euro,
    inputPriceBelow20cm = 10.39.euro,
    inputPriceBelow22cm = 11.91.euro,
    inputPriceAbove22cm = 26.90.euro,
    surCharge = surCharges.constructionSurCharge,
  )

  val wallboxInstallationPriceBase: PriceWithProfit = PriceWithProfit.surcharge(375.00.euro, 40.percent)
  val wallboxInstallationPriceWorkingTime: Duration = 5.hours
  val wallboxInstallationPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(wallboxInstallationPriceBase, wallboxInstallationPriceWorkingTime, workingHourPrice)

  /**
   *
   */
  @Reselling
  val wallBoxInputPrices: InfoForType<Wallbox, Money> = InfoForType {
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("9e0ba349-baf6-464e-96d0-f2dbecab376c")),
        description = "Ladestation KEBA Green Edition KeContact P30 x-Series M&E mit 6 m Typ 2 Kabel",
        details = "M&E zertifizierte AC-Ladestation, zur kWh-Abrechnung, für schnelles und PV-optimiertes Laden von Elektrofahrzeugen im einphasigen oder dreiphasigen Betrieb. Die Ladestation beinhaltet: \n" +
          "- ein 6m langes Typ 2 Ladekabel\n" +
          "- eine Zugangsbeschränkung über RFID\n" +
          "- Ethernet Anschluss (RJ45 / LSA+) \n" +
          "- eine WLAN Schnittstelle für die Smart Home Integration\n" +
          "Eine PV-optimierte Nutzung ist nur in Kombination einem kompatiblen Energie-Management-System möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters0,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.KEBA,
        dataSheetPath = "KEBA Green Edition KeContact P30 x-series M&E.pdf",
      ), 1_397.66.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("115c0f9d-bd80-45ae-9408-f9453c844a88")),
        description = "SMA eCharger 22kW T2 ohne Kabel",
        details = "Dreiphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,38 bis 22 kW von Elektrofahrzeugen. Die Ladestation beinhaltet eine Typ 2 Ladebuchse und eine Zugangsbeschränkung über ein RFID-Kartenleser inklusive 2x SMA RFDI-Karten. \n" +
            "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.\n" + "\n" +
            "Ein Typ 2 Ladekabel ist in diesem Angebot nicht enthalten und muss bauseits gestellt werden.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters0,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        dataSheetPath = "eCharger 22.pdf",
      ), 812.55.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("c0697a60-23a0-4d6e-9f9a-28f9377b24d2")),
        description = "SMA eCharger 22kW T2 5m Kabel",
        details = "Dreiphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,38 bis 22 kW von Elektrofahrzeugen. Die Ladestation beinhaltet eine Typ 2 Ladebuchse mit einem mobilen 5 m AC-Ladekabel Typ 2 und eine Zugangsbeschränkung über ein RFID-Kartenleser inklusive 2x SMA RFDI-Karten. \n" +
            "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters5,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        dataSheetPath = "eCharger 22.pdf",
      ), 950.82.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("6e305799-22a5-40ec-b4b8-170b57160633")),
        description = "SMA eCharger 22kW T2 7,5m Kabel",
        details = "Dreiphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,38 bis 22 kW von Elektrofahrzeugen. Die Ladestation beinhaltet eine Typ 2 Ladebuchse mit einem mobilen 7,5 m AC-Ladekabel Typ 2 und eine Zugangsbeschränkung über ein RFID-Kartenleser inklusive 2x SMA RFDI-Karten. \n" +
            "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters75,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        dataSheetPath = "eCharger 22.pdf",
      ), 965.62.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("28b30fa8-4969-4f0d-bf98-6f0496bb2bd4")),
        description = "SMA eCharger 22kW T2 10m Kabel",
        details = "Dreiphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,38 bis 22 kW von Elektrofahrzeugen. Die Ladestation beinhaltet eine Typ 2 Ladebuchse mit einem mobilen 10 m AC-Ladekabel Typ 2 und eine Zugangsbeschränkung über ein RFID-Kartenleser inklusive 2x SMA RFDI-Karten. \n" +
            "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters10,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        dataSheetPath = "eCharger 22.pdf",
      ), 979.45.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("a333d548-dc50-4d32-995a-7644f7820089")),
        description = "Ladestation Heidelberg Energy Control 11kW T2 5m",
        details = "Dreiphasige Wallbox mit einer maximalen Ladeleistung von 11kW inklusive einem 5 m Ladekabel mit Typ 2 Stecker.",
        chargingCapacity = Wallbox.ChargingCapacity.KW11,
        cableLength = Wallbox.CableLength.Meters5,
        manufacturer = Manufacturer.SMA,
      ), 456.99.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("4f169ee0-ce8e-49f6-97e6-ce473d893627")),
        description = "Ladestation Heidelberg Energy Control 11kW T2 7,5m",
        details = "Dreiphasige Wallbox mit einer maximalen Ladeleistung von 11kW inklusive einem 7,5 m Ladekabel mit Typ 2 Stecker.",
        chargingCapacity = Wallbox.ChargingCapacity.KW11,
        cableLength = Wallbox.CableLength.Meters75,
        manufacturer = Manufacturer.SMA,
      ), 506.99.euro
    )

    /**
     * The following wallboxes are end of life and will not be available anymore.
     */
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("c7592ca3-5f88-4b7f-8e80-2198c623363c")),
        description = "Ladestation SMA EV Charger 22kW T2 5m",
        details = "Dreiphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,3 bis 22 kW von Elektrofahrzeugen. Inklusive 5 m Ladekabel mit Typ 2 Stecker.\n" +
          "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters5,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 1_000.00.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("9fa805b2-786c-44d5-a790-b553be7861cf")),
        description = "Ladestation SMA EV Charger 22kW T2 7,5m",
        details = "Dreiphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,3 bis 22 kW von Elektrofahrzeugen. Inklusive 7,5 m Ladekabel mit Typ 2 Stecker.\n" +
          "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters75,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 1_324.16.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("fe4553f5-4a78-4f99-9c5e-db0f482ee5e4")),
        description = "Ladestation SMA EV Charger 22kW T2 10m",
        details = "Dreiphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,3 bis 22 kW von Elektrofahrzeugen. Inklusive 10 m Ladekabel mit Typ 2 Stecker.\n" +
          "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters10,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 1_381.78.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("e896ecde-2a54-4381-a570-1bebae33d872")),
        description = "Ladestation SMA EV Charger 7,4kW T2 5m",
        details = "Einphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,3 bis 7,4 kW von Elektrofahrzeugen. Inklusive 5 m Ladekabel mit Typ 2 Stecker.\n" +
          "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW74,
        cableLength = Wallbox.CableLength.Meters5,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 955.60.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("1d624a50-1eb4-4ec6-93f4-d15371dfefcb")),
        description = "Ladestation SMA EV Charger 7,4kW T2 7,5m",
        details = "Einphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,3 bis 7,4 kW von Elektrofahrzeugen. Inklusive 7,5 m Ladekabel mit Typ 2 Stecker.\n" +
          "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW74,
        cableLength = Wallbox.CableLength.Meters75,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 1_019.19.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("c3672491-4a5d-41a9-bde2-e257d36114cd")),
        description = "Ladestation SMA EV Charger 7,4kW T2 10m",
        details = "Einphasige AC-Ladestation für schnelles und PV- optimiertes Laden mit einer konfigurierbaren Ladeleistung von 1,3 bis 7,4 kW von Elektrofahrzeugen. Inklusive 10 m Ladekabel mit Typ 2 Stecker.\n" +
          "Nur in Kombination mit dem Sunny Home Manager 2.0 möglich.",
        chargingCapacity = Wallbox.ChargingCapacity.KW74,
        cableLength = Wallbox.CableLength.Meters10,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), 1_053.48.euro
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("9f68e393-aede-4ee6-be67-bef63f2312f8")),
        description = "Ladestation ABL eMH1  11kW T2 6,35m",
        details = "Dreiphasige Wallbox mit einer maximalen Ladeleistung von 11kW inklusive einem 6,35 m Ladekabel mit Typ 2 Stecker.",
        chargingCapacity = Wallbox.ChargingCapacity.KW11,
        cableLength = Wallbox.CableLength.Meters5,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ),
      461.99.euro,
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("1e5a1dd8-ce2c-4790-adef-83f5886c42c7")),
        description = "Heidelberg Energy Control",
        details = null,
        chargingCapacity = Wallbox.ChargingCapacity.KW11,
        cableLength = Wallbox.CableLength.Meters5,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ),
      650.00.euro,
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("1f981fe2-0ea0-4494-9b2d-ce4916ec3eaa")),
        description = "ABL eMH1",
        details = null,
        chargingCapacity = Wallbox.ChargingCapacity.KW11,
        cableLength = Wallbox.CableLength.Meters5,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ),
      650.00.euro,
    )
    add(
      Wallbox(
        id = WallboxId(Uuid.parse("9e7de884-85de-47f6-a29c-8d628c54d391")),
        description = "SMA EV Charger",
        details = "Dreiphasige AC-Ladestation für schnelles und PV-optimiertes Laden",
        chargingCapacity = Wallbox.ChargingCapacity.KW22,
        cableLength = Wallbox.CableLength.Meters5,
        type = Wallbox.Type.Smart,
        manufacturer = Manufacturer.SMA,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ),
      1180.00.euro,
    )
  }

  /**
   * The price per module depending on the difficulty of the assembly
   */
  val assemblyDifficultyWorkingTimes: InfoForType<AssemblyDifficulty, Duration> = InfoForType {
    add(PriceListInfo.assemblyDifficultyEasy, 1.3.hours)
    add(PriceListInfo.assemblyDifficultyMedium, 112.minutes)
    add(PriceListInfo.assemblyDifficultyHard, 2.38.hours)
  }

  /**
   * The prices for the metal roof tiles
   */
  @Reselling
  val metalRoofTileInputPrices: InfoForType<MetalRoofTile, Money> = LizergyAvailableRoofTilesFactory.createPrices()
  val aufschlagColoredMetalPlatesInputsPrices: InfoForType<MetalRoofTileColor, Money> = InfoForType {
    add(MetalRoofTileColor(MetalRoofTileColorId(Uuid.parse("7e6f27f0-988d-421c-b452-9dac46d59fca")), "Rot"), 4.42.euro)
    add(MetalRoofTileColor(MetalRoofTileColorId(Uuid.parse("94098bd7-b47e-45b7-9383-5bb20500615b")), "Braun"), 4.42.euro)
    add(MetalRoofTileColor(MetalRoofTileColorId(Uuid.parse("38299ce0-b3f9-437e-8b13-89d04ff757e8")), "Schwarz-Grau"), 4.42.euro)
  }

  @Reselling
  val monitoringSellingPrices: MonitoringPrices = MonitoringPrices(
    priceBelow10kWp = PriceWithProfit(50.00.euro, 115.00.euro),
    priceBelow20kWp = PriceWithProfit(50.00.euro, 138.00.euro),
    priceBelow30kWp = PriceWithProfit(50.00.euro, 161.00.euro),
    priceElse = PriceWithProfit(50.00.euro, 230.00.euro),
    additionalCostsWithBattery = PriceWithProfit(50.00.euro, 92.00.euro)
  )
  val monitoringDataSheetPath: String = "Sunny_Portal.pdf"

  val scaffoldingWorkingTime: Duration = 0.17.hours
  val scaffoldingPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(PriceWithProfit.Zero, scaffoldingWorkingTime, workingHourPrice)

  val sitePreparationInputPrice: Money = 690.00.euro
  val sitePreparationPrice: PriceWithProfit = PriceWithProfit.noProfit(sitePreparationInputPrice)

  val dachStaenderIsolierungInputPrice: PriceWithProfit = PriceWithProfit.noProfit(420.00.euro)
  val dachStaenderIsolierungWorkingTime: Duration = 1.hours
  val dachStaenderIsolierungPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(dachStaenderIsolierungInputPrice, dachStaenderIsolierungWorkingTime, workingHourPrice)

  @Reselling
  val ueberSpannungsSchutzInputPrice: PriceWithProfit = PriceWithProfit.surcharge(81.00.euro, surCharges.constructionSurCharge)
  val ueberSpannungsSchutzWorkingTime: Duration = 0.66.hours
  val ueberSpannungsSchutzPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(ueberSpannungsSchutzInputPrice, ueberSpannungsSchutzWorkingTime, workingHourPrice)

  val ueberSpannungsSchutzExtraWennExternerBlitzschutzVorhandenInputPrice: PriceWithProfit = PriceWithProfit.surcharge(121.00.euro, surCharges.constructionSurCharge)
  val ueberSpannungsSchutzExtraWennExternerBlitzschutzVorhandenWorkingTIme: Duration = 0.66.hours
  val ueberSpannungsSchutzExtraWennExternerBlitzschutzVorhandenPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(ueberSpannungsSchutzExtraWennExternerBlitzschutzVorhandenInputPrice, ueberSpannungsSchutzExtraWennExternerBlitzschutzVorhandenWorkingTIme, workingHourPrice)

  // Überspannungsschutz AC: EK 182.00€ + Zubehör 36.50€ = 218.50€, VK-Faktor 1.4
  @Reselling
  val ueberSpannungsSchutzACInputPrice: PriceWithProfit = PriceWithProfit.surcharge(218.50.euro, surCharges.constructionSurCharge)
  val ueberSpannungsSchutzACWorkingTime: Duration = 2.hours
  val ueberSpannungsSchutzACPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(ueberSpannungsSchutzACInputPrice, ueberSpannungsSchutzACWorkingTime, workingHourPrice)

  // Überspannungsschutz AC mit äußerem Blitzschutz: EK 361.00€ + Zubehör 36.50€ = 397.50€, VK-Faktor 1.4
  @Reselling
  val ueberSpannungsSchutzACMitBlitzschutzInputPrice: PriceWithProfit = PriceWithProfit.surcharge(397.50.euro, surCharges.constructionSurCharge)
  val ueberSpannungsSchutzACMitBlitzschutzWorkingTime: Duration = 2.hours
  val ueberSpannungsSchutzACMitBlitzschutzPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(ueberSpannungsSchutzACMitBlitzschutzInputPrice, ueberSpannungsSchutzACMitBlitzschutzWorkingTime, workingHourPrice)

  val moveSatelliteDishInputPrice: PriceWithProfit = PriceWithProfit.surcharge(50.00.euro, surCharges.constructionSurCharge)
  val moveSatelliteDishWorkingTime: Duration = 5.hours
  val moveSatelliteDishPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(moveSatelliteDishInputPrice, moveSatelliteDishWorkingTime, workingHourPrice)

  val removeAntennaWorkingTime: Duration = 1.5.hours
  val removeAntennaPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(PriceWithProfit.Zero, removeAntennaWorkingTime, workingHourPrice)

  val integrateInverterIntoNetworkInputPrice: PriceWithProfit = PriceWithProfit.surcharge(50.00.euro, surCharges.constructionSurCharge)
  val integrateInverterIntoNetworkWorkingTime: Duration = 2.5.hours
  val integrateInverterIntoNetworkPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(integrateInverterIntoNetworkInputPrice, integrateInverterIntoNetworkWorkingTime, workingHourPrice)

  val perKilometerTravelledInputPrice: Money = 1.30.euro
  val perKilometerTravelledPrice: PriceWithProfit = PriceWithProfit.surcharge(perKilometerTravelledInputPrice, surCharges.constructionSurCharge)

  @Reselling
  val optimizerInputPrice: PriceWithProfit = PriceWithProfit.surcharge(48.00.euro, surCharges.constructionSurCharge)
  val optimizerWorkingTime: Duration = 0.1.hours
  val optimizerPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(optimizerInputPrice, optimizerWorkingTime, workingHourPrice)

  val zuschlagKabelwegInputPrice: PriceWithProfit = PriceWithProfit.surcharge(5.00.euro, surCharges.constructionSurCharge)
  val zuschlagKabelwegWorkingTime: Duration = 0.15.hours
  val zuschlagKabelwegPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(zuschlagKabelwegInputPrice, zuschlagKabelwegWorkingTime, workingHourPrice)

  @PerModule
  val aufschlagKreuzverbundInputPrice: PriceWithProfit = PriceWithProfit.surcharge(25.39.euro, surCharges.constructionSurCharge)
  val aufschlagKreuzverbundWorkingTime: Duration = 0.1.hours
  val aufschlagKreuzverbundPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(aufschlagKreuzverbundInputPrice, aufschlagKreuzverbundWorkingTime, workingHourPrice)

  /**
   * The starting up price
   */
  val startingUpInputPrice: PriceWithProfit = PriceWithProfit.surcharge(350.0.euro, surCharges.constructionSurCharge)
  val startingUpWorkingTime: Duration = 10.hours
  val startingUpPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(startingUpInputPrice, startingUpWorkingTime, workingHourPrice)

  val sunnyHomeManagerInputPrice: PriceWithProfit = PriceWithProfit.surcharge(402.54.euro, surCharges.constructionSurCharge)
  val sunnyHomeManagerWorkingTime: Duration = 3.hours
  val sunnyHomeManagerPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(sunnyHomeManagerInputPrice, sunnyHomeManagerWorkingTime, workingHourPrice)
  val sunnyHomeManagerExtraPrice: PriceWithProfit = PriceWithProfit.surcharge(50.euro, surCharges.constructionSurCharge)
  val sunnyHomeManagerDataSheetPath: String = "SunnyHome Manager 2.0.pdf"

  val ibcHomeOneHubInputPrice: PriceWithProfit = PriceWithProfit.surcharge(215.25.euro, surCharges.constructionSurCharge)
  val ibcHomeOneHubWorkingTime: Duration = 3.hours
  val ibcHomeOneHubPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(ibcHomeOneHubInputPrice, ibcHomeOneHubWorkingTime, workingHourPrice)
  val ibcHomeOneHubExtraPrice: PriceWithProfit = PriceWithProfit.surcharge(50.euro, surCharges.constructionSurCharge)
  val ibcHomeOneHubDataSheetPath: String = "HomeOne_Hub_BT_RE1.pdf"

  val homeManagerPrices: InfoForType<Manufacturer, PriceWithProfitAndWorkingTime> = InfoForType {
    add(
      Manufacturer.SMA,
      PriceWithProfitAndWorkingTime(sunnyHomeManagerInputPrice, sunnyHomeManagerWorkingTime, workingHourPrice)
    )
    add(
      Manufacturer.IBC,
      PriceWithProfitAndWorkingTime(ibcHomeOneHubInputPrice, ibcHomeOneHubWorkingTime, workingHourPrice)
    )
  }
  val homeManagerExtraPrices: InfoForType<Manufacturer, PriceWithProfit> = InfoForType {
    add(
      Manufacturer.SMA,
      sunnyHomeManagerExtraPrice
    )
    add(
      Manufacturer.IBC,
      ibcHomeOneHubExtraPrice
    )
  }
  val homeManagerDataSheetPaths: InfoForType<Manufacturer, String> = InfoForType {
    add(
      Manufacturer.SMA,
      sunnyHomeManagerDataSheetPath
    )
    add(
      Manufacturer.IBC,
      ibcHomeOneHubDataSheetPath
    )
  }

  /**
   * The prices for all available inverters
   */
  @Reselling
  val heaterRodInputPrices: InfoForType<HeaterRod, MoneyAndTime> = InfoForType {
    add(
      HeaterRod(
        id = HeaterRodId(Uuid.parse("56db844d-f77a-4dc5-b841-348c9ce5b747")),
        description = "Heizstab myPV AC ELWA-2",
        heatingCapacity = HeaterRod.HeatingCapacity.KW3_5,
        length = 46.0,
        diameter = 1.5,
        dataSheetPath = "AC ELWA 2.pdf",
      ),
      MoneyAndTime(630.72.euro, 3.hours),
    )
    add(
      HeaterRod(
        id = HeaterRodId(Uuid.parse("223e4b8b-6b2d-4524-ad23-a4da2d39fbef")),
        description = "Heizstab myPV AC elwa-e",
        heatingCapacity = HeaterRod.HeatingCapacity.KW3,
        length = 45.0,
        diameter = 1.5,
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ),
      MoneyAndTime(560.38.euro, 3.hours),
    )
  }

  val waermepumpenanbindungInputPrice: PriceWithProfit = PriceWithProfit.surcharge(273.26.euro, surCharges.waermepumpenSurCharge)
  val waermepumpenanbindungWorkingTime: Duration = 6.hours
  val waermepumpenanbindungPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(waermepumpenanbindungInputPrice, waermepumpenanbindungWorkingTime, workingHourPrice)

  val smaEnergyMeterInputPrice: PriceWithProfit = PriceWithProfit.surcharge(264.87.euro, surCharges.energyMeterSurCharge)
  val smaEnergyMeterWorkingTime: Duration = 2.hours
  val smaEnergyMeterPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(smaEnergyMeterInputPrice, smaEnergyMeterWorkingTime, workingHourPrice)
  val smaEnergyMeterDataSheetPath: String = "SMA Energy Meter & CT.pdf"

  val erdspiessSetzenInputPrice: PriceWithProfit = PriceWithProfit.surcharge(132.07.euro, surCharges.energyMeterSurCharge)
  val erdspiessSetzenWorkingTime: Duration = 4.hours
  val erdspiessSetzenPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(erdspiessSetzenInputPrice, erdspiessSetzenWorkingTime, workingHourPrice)

  val slsNachruestenInputPrice: PriceWithProfit = PriceWithProfit.surcharge(85.00.euro, surCharges.constructionSurCharge)
  val slsNachruestenWorkingTime: Duration = 3.hours
  val slsNachruestenPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(slsNachruestenInputPrice, slsNachruestenWorkingTime, workingHourPrice)

  val neuerZaehlerschrankInputPrice: PriceWithProfit = PriceWithProfit.surcharge(2_180.00.euro, surCharges.constructionSurCharge)
  val neuerZaehlerschrankWorkingTime: Duration = 16.hours
  val neuerZaehlerschrankPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(neuerZaehlerschrankInputPrice, neuerZaehlerschrankWorkingTime, workingHourPrice)

  val enwg14aIntegrationInZaehlerschrankInputPrice: PriceWithProfit = PriceWithProfit.surcharge(214.28.euro, surCharges.constructionSurCharge)
  val enwg14aIntegrationInZaehlerschrankWorkingTime: Duration = 2.hours
  val enwg14aIntegrationInZaehlerschrankPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(enwg14aIntegrationInZaehlerschrankInputPrice, enwg14aIntegrationInZaehlerschrankWorkingTime, workingHourPrice)

  val enwg14aIntegrationInAufputzgehaeuseInputPrice: PriceWithProfit = PriceWithProfit.surcharge(260.71.euro, surCharges.constructionSurCharge)
  val enwg14aIntegrationInAufputzgehaeuseWorkingTime: Duration = 3.hours
  val enwg14aIntegrationInAufputzgehaeusePrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(enwg14aIntegrationInAufputzgehaeuseInputPrice, enwg14aIntegrationInAufputzgehaeuseWorkingTime, workingHourPrice)

  val zaehlerschrankZusammenlegenInputPrice: PriceWithProfit = PriceWithProfit.surcharge(50.00.euro, surCharges.constructionSurCharge)
  val zaehlerschrankZusammenlegenWorkingTime: Duration = 3.hours
  val zaehlerschrankZusammenlegenPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(zaehlerschrankZusammenlegenInputPrice, zaehlerschrankZusammenlegenWorkingTime, workingHourPrice)

  val einbauUnterverteilerInputPrice: PriceWithProfit = PriceWithProfit.surcharge(150.00.euro, surCharges.constructionSurCharge)
  val einbauUnterverteilerWorkingTime: Duration = 4.hours
  val einbauUnterverteilerPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(einbauUnterverteilerInputPrice, einbauUnterverteilerWorkingTime, workingHourPrice)

  val einbauDigitalerZaehlerInputPrice: PriceWithProfit = PriceWithProfit.surcharge(113.42.euro, surCharges.constructionSurCharge)
  val einbauDigitalerZaehlerWorkingTime: Duration = 1.5.hours
  val einbauDigitalerZaehlerPrice: PriceWithProfitAndWorkingTime = PriceWithProfitAndWorkingTime(einbauDigitalerZaehlerInputPrice, einbauDigitalerZaehlerWorkingTime, workingHourPrice)

  @PerModule
  val aufpreisSchienensystemInputPrice: Money = 10.99.euro
  val aufpreisSchienensystemPrice: PriceWithProfit = PriceWithProfit.surcharge(aufpreisSchienensystemInputPrice, surCharges.energyMeterSurCharge)

  @PerModule
  val aufpreisSchwarzeKlemmeInputPrice: Money = 1.82.euro
  val aufpreisSchwarzeKlemmePrice: PriceWithProfit = PriceWithProfit.surcharge(aufpreisSchwarzeKlemmeInputPrice, surCharges.klemmenSurCharge)

  val blitzschutzIntegrierenPrice: PriceWithProfit = PriceWithProfit.surcharge(900.00.euro, surCharges.constructionSurCharge)

  @Reselling
  val independenceManagerTypeInputPricesWithHigherSurCharge: InfoForType<IndependenceManagerType, MoneyAndTime> = InfoForType {
    add(
      LizergyIndependenceManagerType(
        id = IndependenceManagerId(Uuid.parse("d69fcc30-8d04-442c-8fff-83cef60f7905")),
        description = "Ersatzstromsystem in Kombination mit SMA Sunny Tripower Smart Energy Serie (Hybrid-Wechselrichter)",
        details = """
          Das Ersatzstromsystem baut mit Hilfe der PV-Anlage und des Batteriespeichers eine Stromversorgung für ausgewählte Verbraucher auf. Es beinhaltet keine USV (Unterbrechungsfreie Stromversorgung) und muss im Ersatzstromfall manuell über einen im Zählerplatz liegenden Umschalter eingeschalten werden. Die Auslegung der PV-Anlage muss ggf. angepasst werden. Nach Auftragsbestätigung kundenseitig und vor Zusage seitens Lizergy muss die elektrische Anlage vor Ort auf Machbarkeit durch einen unserer Mitarbeiter überprüft werden. Durch die Prüfung ihrer Installation auf Ersatzstromfähigkeit ergeben sich ggf. notwendig werdende Änderungen, diese werden nach tatsächlichem Aufwand abgerechnet. Ggf. fallen dadurch Mehrkosten an.
        """.trimIndent(),
        requiredInverterTypes = listOf(setOf(hybridInverterSTP5_0, hybridInverterSTP6_0, hybridInverterSTP8_0, hybridInverterSTP10_0)),
        assemblyPortfolioDescription = "Manuelle Umschaltung für ausgewählte Verbraucher mit SMA STP XX SE Hybrid-Wechselrichter",
      ), MoneyAndTime(480.00.euro, 16.hours)
    )

    // Deprecated elements
    add(
      LizergyIndependenceManagerType(
        id = IndependenceManagerId(Uuid.parse("f23c3a18-2d45-4388-a03c-8aa16dd506b8")),
        description = "Ersatzstromsystem in Kombination mit SMA Sunny Boy Smart Energy Serie (Hybrid-Wechselrichter)",
        details = """
          Das Ersatzstromsystem baut mit Hilfe der PV-Anlage und des Batteriespeichers eine Stromversorgung für ausgewählte Verbraucher auf. Es beinhaltet keine USV (Unterbrechungsfreie Stromversorgung) und muss im Ersatzstromfall manuell über einen im Zählerplatz liegenden Umschalter eingeschalten werden. Die Auslegung der PV-Anlage muss ggf. angepasst werden. Nach Auftragsbestätigung kundenseitig und vor Zusage seitens Lizergy muss die elektrische Anlage vor Ort auf Machbarkeit durch einen unserer Mitarbeiter überprüft werden. Durch die Prüfung ihrer Installation auf Ersatzstromfähigkeit ergeben sich ggf. notwendig werdende Änderungen, diese werden nach tatsächlichem Aufwand abgerechnet. Ggf. fallen dadurch Mehrkosten an.
        """.trimIndent(),
        requiredInverterTypes = listOf(setOf(hybridInverterSB3_6, hybridInverterSB4_0, hybridInverterSB5_0)),
        assemblyPortfolioDescription = "Manuelle Umschaltung für ausgewählte Verbraucher mit SMA STP XX SE Hybrid-Wechselrichter",
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), MoneyAndTime(480.00.euro, 16.hours)
    )
  }

  @Reselling
  val independenceManagerTypeInputPrices: InfoForType<IndependenceManagerType, MoneyAndTime> = InfoForType {
    add(
      LizergyIndependenceManagerType(
        id = IndependenceManagerId(Uuid.parse("eb446b84-f940-4204-8696-51d31633f420")),
        description = "Ersatzstromsystem in Kombination mit SMA SunnyIsland Batterie-Wechselrichtern",
        details = """
          Das Ersatzstromsystem baut mit Hilfe der PV-Anlage und des Batteriespeichers eine Stromversorgung auf (nur für Wechselstromverbraucher). Es beinhaltet keine USV (Unterbrechungsfreie Stromversorgung). Die Auslegung der PV-Anlage muss ggf. angepasst werden. Nach Auftragsbestätigung kundenseitig und vor Zusage seitens Lizergy muss die elektrische Anlage vor Ort auf Machbarkeit durch einen Elektromeister überprüft werden. Durch die Prüfung ihrer Installation auf Ersatzstromfähigkeit ergeben sich ggf. notwendig werdende Änderungen, diese werden nach tatsächlichem Aufwand abgerechnet. Ggf. fallen dadurch Mehrkosten an.
        """.trimIndent(),
        requiredInverterTypes = buildList {
          //Requires normal inverter...
          add(setOf(inverterSB1_5, inverterSB2_0, inverterSB2_5, inverterSB3_0, inverterSB3_6, inverterSB4_0, inverterSB5_0))
          //...and battery inverter
          add(setOf(batteryInverterSunnyIsland4_4, batteryInverterSunnyIsland6_0))
        },
        assemblyPortfolioDescription = "Umschaltung mit SMA SunnyIsland Batterie-Wechselrichter",
      ), MoneyAndTime(1_100.00.euro, 12.hours)
    )
    add(
      OtherIndependenceManagerType(
        id = IndependenceManagerId(Uuid.parse("757adbe7-f53d-4441-8e21-e34eafae83b4")),
        description = "Ersatzstromsystem für einzelne Verbraucher mit Sunny Tripower Smart Energy Serie",
        details = """
          Das Ersatzstromsystem baut automatisiert mit Hilfe der PV-Anlage und des Batteriespeichers eine Stromversorgung für bis zu 3 Verbraucher auf. Es beinhaltet keine USV (Unterbrechungsfreie Stromversorgung). Die Auslegung der PV-Anlage muss ggf. angepasst werden.
        """.trimIndent(),
        requiredInverterTypes = listOf(setOf(hybridInverterSTP5_0, hybridInverterSTP6_0, hybridInverterSTP8_0, hybridInverterSTP10_0)),
        assemblyPortfolioDescription = "Automatische Umschaltung für drei Stromkreise mit SMA STP XX SE Hybrid-Wechselrichter",
      ), MoneyAndTime(321.00.euro, 6.hours)
    )

    // Deprecated elements
    add(
      OtherIndependenceManagerType(
        id = IndependenceManagerId(Uuid.parse("87272578-451c-4066-ac8a-ef3c2be58613")),
        description = "Ersatzstromsystem für einzelne Verbraucher mit Sunny Boy Smart Energy Serie",
        details = """
          Das Ersatzstromsystem baut automatisiert mit Hilfe der PV-Anlage und des Batteriespeichers eine Stromversorgung für bis zu 3 Verbraucher auf. Es beinhaltet keine USV (Unterbrechungsfreie Stromversorgung). Die Auslegung der PV-Anlage muss ggf. angepasst werden.
        """.trimIndent(),
        requiredInverterTypes = listOf(setOf(hybridInverterSB3_6, hybridInverterSB4_0, hybridInverterSB5_0)),
        assemblyPortfolioDescription = "Automatische Umschaltung für drei Stromkreise mit SMA STP XX SE Hybrid-Wechselrichter",
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), MoneyAndTime(321.00.euro, 6.hours)
    )
    add(
      OtherIndependenceManagerType(
        id = IndependenceManagerId(Uuid.parse("498903c9-81bf-4d91-91b0-4c911ad389eb")),
        description = "Steckdose für Ersatzstromsystem mit Sunny Boy Storage",
        details = """
          Nur in Kombination mit einen Sunny Boy Storage möglich. Über eine Steckdose wird mit Hilfe  des Batteriespeichers ein Ersatzstromsystem (nur für Wechselstromverbraucher) aufgebaut. Die Steckdose führt nur Spannung bis der Batteriespeicher leer ist. Der Batteriespeicher wird im Ersatzstromfall nicht nachgeladen. Es beinhaltet keine USV (Unterbrechungsfreie Stromversorgung).
        """.trimIndent(),
        requiredInverterTypes = listOf(setOf(batteryInverterSBStorage3_7, batteryInverterSBStorage5_0)),
        assemblyPortfolioDescription = "Eine Steckdose mit SMA Sunny Boy Storage Batterie-Wechselrichter",
        lifeCycleState = LizergyLifeCycleState.EndOfLife,
      ), MoneyAndTime(50.00.euro, 3.hours)
    )
  }

  /**
   * Each list is ordered on its own, so the merge sorts end-of-life entries behind the active ones of both lists.
   */
  fun createIndependenceManagerTypeSellingPrices(): InfoForType<IndependenceManagerType, PriceWithProfitAndWorkingTime> {
    val sellingPrices: List<Pair<IndependenceManagerType, PriceWithProfitAndWorkingTime>> = buildList {
      independenceManagerTypeInputPricesWithHigherSurCharge.forEach { independenceManagerType, inputMoneyAndTime ->
        val materialPrice = PriceWithProfit.surcharge(inputMoneyAndTime.money, surCharges.specialSurCharge)
        add(independenceManagerType to PriceWithProfitAndWorkingTime(materialPrice, inputMoneyAndTime.time, workingHourPrice))
      }
      independenceManagerTypeInputPrices.forEach { independenceManagerType, inputMoneyAndTime ->
        val materialPrice = PriceWithProfit.surcharge(inputMoneyAndTime.money, surCharges.inverterSurCharge)
        add(independenceManagerType to PriceWithProfitAndWorkingTime(materialPrice, inputMoneyAndTime.time, workingHourPrice))
      }
    }

    return InfoForType(sellingPrices.sortedBy { it.first.isEndOfLife() }.toMap())
  }

  fun createBatteryConfigurationPrices(
    batteryStorageWorkingTimePrices: InfoForType<BatteryStorageType, WorkingTimesWithPrice>,
    batteryStorageExtensionWorkingTimePrices: InfoForType<BatteryStorageExtensionType, WorkingTimesWithPrice>,
  ): InfoForType<BatteryConfiguration, PriceWithProfitAndWorkingTime> {
    return InfoForType(
      batteryAndInverterCombinations.flatMap { batteryAndInverter ->
        //The base price
        val battery = batteryAndInverter.battery
        val inverter = batteryAndInverter.inverter
        val priceForInverter = when (inverter) {
          is HybridInverter -> hybridInverterInputPrices.value(inverter)
          is BasicBatteryInverter -> batteryInverterInputPrices.value(inverter)
        }
        val priceForBattery = battery?.let { batteryStorageInputPrices.value(battery) } ?: Money.Zero
        val priceForBatteryAndInverter = PriceWithProfit.surcharge(priceForBattery + priceForInverter, surCharges.batteryStorageSurCharge)
        val workForBatteryStorage = battery?.let { batteryStorageWorkingTimePrices.value(battery) } ?: WorkingTimesWithPrice.Zero
        val priceAndWorkForBatteryStorage = PriceWithProfitAndWorkingTime(priceForBatteryAndInverter, workForBatteryStorage)

        //Calculate the UUID for the configuration *without* any extension
        val newId = BatteryConfigurationId(batteryAndInverter.uuid.xor(351351351351351135L, 651238907123409871L))

        //Only the battery - without any extension
        sequenceOf(BatteryConfiguration(id = newId, battery = battery, extension = null, inverterType = inverter) to priceAndWorkForBatteryStorage)
          .plus(
            //Find the extensions
            availableBatteryStorageExtensionsFor(battery).map { extensionType ->
              //Create a combination with each possible extension

              val extensionPrice = PriceWithProfit.surcharge(batteryStorageExtensionInputPrices.value(extensionType), surCharges.batteryStorageSurCharge)
              val extensionWork = batteryStorageExtensionWorkingTimePrices.value(extensionType)
              val priceAndWorkForExtension = PriceWithProfitAndWorkingTime(extensionPrice, extensionWork)

              //Combine both UUIDs to get a (hopefully) unique UUID that can be reproduced
              val combinedId = BatteryConfigurationId(batteryAndInverter.uuid.xor(extensionType.uuid))
              BatteryConfiguration(id = combinedId, battery = battery, extension = extensionType, inverterType = inverter) to (priceAndWorkForBatteryStorage + priceAndWorkForExtension)
            }
          )
      }.toMap()
    )
  }

  fun createBatteryPrices(batteryStorageWorkingTimePrices: InfoForType<BatteryStorageType, WorkingTimesWithPrice>): InfoForType<BatteryStorageType, PriceWithProfitAndWorkingTime> {
    return InfoForType(
      batteryStorageInputPrices.available.associateWith { battery -> //The base price
        val priceForBattery = batteryStorageInputPrices.value(battery)
        val priceForBatteryAndInverter = PriceWithProfit.surcharge(priceForBattery, surCharges.batteryStorageSurCharge)
        val workForBatteryStorage = batteryStorageWorkingTimePrices.value(battery)
        PriceWithProfitAndWorkingTime(priceForBatteryAndInverter, workForBatteryStorage)
      }
    )
  }

  fun availableBatteryStorageExtensionsFor(battery: BatteryStorageType?): List<BatteryStorageExtensionType> {
    return batteryStorageExtensionInputPrices.available.filter { batteryStorageExtensionType ->
      batteryStorageExtensionType.extensionFor.contains(battery)
    }
  }

  /**
   * Calculates the price list
   */
  fun createPriceList(): StaticPriceList {

    val moduleSellingPrices: Prices<PvModule> = moduleInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.modulesSurCharge)
    }

    val inverterSellingPrices: InfoForType<Inverter, PriceWithProfit> = inverterInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.inverterSurCharge)
    }
    val hybridInverterSellingPrices: InfoForType<HybridInverter, PriceWithProfit> = hybridInverterInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.inverterSurCharge)
    }
    val batteryInverterSellingPrices: InfoForType<BasicBatteryInverter, PriceWithProfit> = batteryInverterInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.inverterSurCharge)
    }

    val batteryStorageWorkingTimePrices: InfoForType<BatteryStorageType, WorkingTimesWithPrice> = batteryStorageWorkingTimes.map { _, workingTime ->
      WorkingTimesWithPrice(workingTime, workingHourPrice)
    }

    val batteryStorageExtensionWorkingTimePrices: InfoForType<BatteryStorageExtensionType, WorkingTimesWithPrice> = batteryStorageExtensionWorkingTimes.map { _, workingTime ->
      WorkingTimesWithPrice(workingTime, workingHourPrice)
    }

    //Create the battery storage material prices
    val batteryConfigurationPrices: InfoForType<BatteryConfiguration, PriceWithProfitAndWorkingTime> = createBatteryConfigurationPrices(batteryStorageWorkingTimePrices, batteryStorageExtensionWorkingTimePrices)
    val batteryStoragePrices: InfoForType<BatteryStorageType, PriceWithProfitAndWorkingTime> = createBatteryPrices(batteryStorageWorkingTimePrices)
    val batteryStorageExtensionPrices = batteryStorageExtensionInputPrices.map { it, inputPrice ->
      PriceWithProfitAndWorkingTime(PriceWithProfit.surcharge(inputPrice, surCharges.batteryStorageSurCharge), batteryStorageExtensionWorkingTimePrices[it])
    }

    val batteryStorageExtraPrices = batteryStorageExtraInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.batteryStorageSurCharge)
    }
    val batteryStorageExtensionExtraPrices = batteryStorageExtensionExtraInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.batteryStorageSurCharge)
    }


    @PerModule
    val substructurePerRoofTypeSellingPrices = substructurePerRoofTypeInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.substructureSurCharge)
    }

    val assemblyDifficultySellingPrices = assemblyDifficultyWorkingTimes.map { _, duration ->
      PriceWithProfitAndWorkingTime(PriceWithProfit.Zero, duration, workingHourPrice)
    }

    val heaterRodSellingPrices: InfoForType<HeaterRod, PriceWithProfitAndWorkingTime> = heaterRodInputPrices.map { _, inputMoneyAndTime ->
      val materialPrice = PriceWithProfit.surcharge(inputMoneyAndTime.money, surCharges.constructionSurCharge)
      PriceWithProfitAndWorkingTime(materialPrice, inputMoneyAndTime.time, workingHourPrice)
    }

    val wallBoxesSellingPrices = wallBoxInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.wallBoxesSurCharge)
    }

    val metalRoofTilesSellingPrices = metalRoofTileInputPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.metalRoofTilesSurCharge)
    }

    val aufschlagColoredMetalPlatesSellingPrices = aufschlagColoredMetalPlatesInputsPrices.map { _, inputPrice ->
      PriceWithProfit.surcharge(inputPrice, surCharges.metalRoofTilesSurCharge)
    }

    return StaticPriceList(
      priceListId = PriceListId(Uuid.parse("cf7c0880-3fd9-43e9-b28e-70564af0dbc6")),
      modulePrices = moduleSellingPrices,
      inverterPrices = inverterSellingPrices,
      hybridInverterPrices = hybridInverterSellingPrices,
      batteryInverterPrices = batteryInverterSellingPrices,
      batteryConfigurationPrices = batteryConfigurationPrices,
      batteryStoragePrices = batteryStoragePrices,
      batteryStorageExtensionPrices = batteryStorageExtensionPrices,
      batteryStorageExtraPrices = batteryStorageExtraPrices,
      batteryStorageExtensionExtraPrices = batteryStorageExtensionExtraPrices,
      assemblyCostsPerModule = substructurePerRoofTypeSellingPrices,
      metalRoofTilePrices = metalRoofTilesSellingPrices,
      aufschlagColoredMetalPlatesPrices = aufschlagColoredMetalPlatesSellingPrices,
      assemblyDifficultyPrices = assemblyDifficultySellingPrices,
      wallboxInstallationPrice = wallboxInstallationPrice,
      wallBoxPrices = wallBoxesSellingPrices,
      monitoringPrices = monitoringSellingPrices,
      monitoringDataSheetPath = monitoringDataSheetPath,
      scaffolding = scaffoldingPrice,
      optionalDiscountPrices = OptionalDiscountPrices.lizergyDefault,
      dachStaenderIsolierung = dachStaenderIsolierungPrice,
      ueberSpannungsSchutz = ueberSpannungsSchutzPrice,
      ueberSpannungsSchutzaWennExternerBlitzschutzVorhanden = ueberSpannungsSchutzExtraWennExternerBlitzschutzVorhandenPrice,
      blitzschutzIntegrieren = blitzschutzIntegrierenPrice,
      ueberSpannungsSchutzAC = ueberSpannungsSchutzACPrice,
      ueberSpannungsSchutzACMitBlitzschutz = ueberSpannungsSchutzACMitBlitzschutzPrice,
      moveSatelliteDish = moveSatelliteDishPrice,
      removeAntenna = removeAntennaPrice,
      workingHoursPerDay = workingHoursPerDay,
      perKilometerTravelled = perKilometerTravelledPrice,
      optimizerPrice = optimizerPrice,
      zuschlagKabelweg = zuschlagKabelwegPrice,
      aufschlagKreuzverbund = aufschlagKreuzverbundPrice,
      startingUpPrice = startingUpPrice,
      assemblyDifficultyWorkingTimes = assemblyDifficultyWorkingTimes,
      availableRoofTiles = LizergyAvailableRoofTilesFactory.create(),
      sitePreparation = sitePreparationPrice,
      integrateInverterIntoNetwork = integrateInverterIntoNetworkPrice,
      homeManagers = homeManagerPrices,
      homeManagerExtras = homeManagerExtraPrices,
      homeManagerDataSheetPaths = homeManagerDataSheetPaths,
      heaterRodPrices = heaterRodSellingPrices,
      waermepumpenanbindung = waermepumpenanbindungPrice,
      smaEnergyMeter = smaEnergyMeterPrice,
      smaEnergyMeterDataSheetPath = smaEnergyMeterDataSheetPath,
      erdspiessSetzen = erdspiessSetzenPrice,
      slsNachruesten = slsNachruestenPrice,
      neuerZaehlerschrank = neuerZaehlerschrankPrice,
      enwg14aIntegrationInZaehlerschrank = enwg14aIntegrationInZaehlerschrankPrice,
      enwg14aIntegrationInAufputzgehaeuse = enwg14aIntegrationInAufputzgehaeusePrice,
      zaehlerschrankZusammenlegen = zaehlerschrankZusammenlegenPrice,
      einbauUnterverteiler = einbauUnterverteilerPrice,
      roofIsolationThicknessPrices = roofIsolationThicknessPrices,
      aufpreisSchienensystemPrice = aufpreisSchienensystemPrice,
      aufpreisSchwarzeKlemmePrice = aufpreisSchwarzeKlemmePrice,
      einbauDigitalerZaehlerPrice = einbauDigitalerZaehlerPrice,
      independenceManagerTypePrices = createIndependenceManagerTypeSellingPrices(),
    )
  }

}
